import { formatCoursePriceText } from "../../utils/coursePricing";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { ThreeDots } from "react-loader-spinner";
import { Formik, Form, Field, ErrorMessage, useFormikContext } from "formik";
import * as Yup from "yup";
import Header from "../../layouts/header";
import {
  emailClassFiveBudgetReport,
  getClassFiveBudget,
  getUserAssessment,
  latestcourseData,
  latestcourseUpdate,
  saveClassFiveBudget,
  saveQuizFirst,
  userAssessment,
} from "../../api/server";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import Footer from "../../layouts/footer";
import ClassFiveBudgetReport from "./ClassFiveBudgetReport";
import RequiredDisclosures from "./RequiredDisclosures";
import ccCourseContent from "../../data/ccCourseContent.json";
import ccClassFiveContent from "../../data/ccClassFiveContent.json";
import ccCourseLists from "../../data/ccCourseLists.json";
import { buildApiUrl } from "../../config/api";
import ClientIdentityBadge from "../GlapApi/ClientIdentityBadge";
import "./Cclatestcourse.css";
import StudentLoanDebtSection from "../Common/StudentLoanDebtSection";
import BudgetValidationFeedback from "../Common/BudgetValidationFeedback";

// ────────────────────────────────────────────────────────────────────────────
const TOTAL_LESSONS = 14;
const isCourseCompletionPayload = (payload) => Boolean(
  payload?.completed === true
  || (
    typeof payload?.message === "string"
    && payload.message.trim().toLowerCase() === "course completed"
  )
);
const CLASS_FIVE_SUBCLASS_COUNT = 9;
const CLASS_FIVE_VIDEO_SUBCLASSES = new Set([1, 2, 3, 4, 9]);

export const getCourseCompletionReturn = (adminUserId) => adminUserId
  ? {
      label: "Back to Client",
      path: `/editstudent?id=${encodeURIComponent(adminUserId)}`,
    }
  : {
      label: "Return to Dashboard",
      path: "/dashboard",
    };
const CLASS_FIVE_NESTED_FORM_SUBCLASSES = new Set([2, 3, 4]);
const CLASS_FIVE_FORM_SUBCLASSES = Object.freeze({
  budget_household: 2,
  monthly_income: 3,
  monthly_expenses: 4,
  debts: 5,
  legal_actions: 6,
  assets: 7,
  review_confirm: 8,
});
const CLASS_FIVE_SIDEBAR_ITEMS = [
  { subClass: 2, label: "Budget: About Your Household", formLabel: "About Your Household" },
  { subClass: 3, label: "Budget: Your Monthly Household Income", formLabel: "Your Monthly Household Income" },
  { subClass: 4, label: "Budget: Monthly Expenses", formLabel: "Monthly Expenses" },
  { subClass: 5, label: "Budget: Debts" },
  { subClass: 6, label: "Budget: Legal Actions and Collections" },
  { subClass: 7, label: "Budget: Assets" },
  { subClass: 8, label: "Budget: Review and Confirm" },
  { subClass: 9, label: "Report" },
];
const PARAGRAPHS_PER_SECTION = 4;
const ORDERED_LIST_ITEMS = new Set(ccCourseLists.ordered);
const UNORDERED_LIST_ITEMS = new Set(ccCourseLists.unordered);

const isDocumentListBlock = (block) =>
  block?.type === "ordered" ||
  block?.type === "bullet" ||
  block?.type === "list" ||
  ORDERED_LIST_ITEMS.has(block?.text) ||
  UNORDERED_LIST_ITEMS.has(block?.text);

const splitDocumentSections = (blocks = []) => {
  if (!blocks.length) return [];

  const hasHeadings = blocks.some((block) => block.type === "heading");

  if (!hasHeadings) {
    const sections = [];
    for (let index = 0; index < blocks.length;) {
      let end = Math.min(index + PARAGRAPHS_PER_SECTION, blocks.length);
      while (
        end < blocks.length &&
        isDocumentListBlock(blocks[end - 1]) &&
        isDocumentListBlock(blocks[end])
      ) {
        end += 1;
      }
      sections.push(blocks.slice(index, end));
      index = end;
    }
    return sections;
  }

  return blocks.reduce((sections, block) => {
    if (block.type === "heading" || sections.length === 0) {
      sections.push([block]);
    } else {
      sections[sections.length - 1].push(block);
    }
    return sections;
  }, []);
};

const LessonDocumentContent = ({ lesson }) => {
  const hasReading = Boolean(lesson?.reading?.length);
  const [activeSection, setActiveSection] = useState(hasReading ? "reading" : "transcript");

  useEffect(() => {
    setActiveSection(lesson?.reading?.length ? "reading" : "transcript");
  }, [lesson]);

  if (!lesson) return null;

  const renderDocumentText = (block) => {
    const text = formatCoursePriceText(block.text);
    const linkText = block?.link?.text;
    const linkHref = block?.link?.href;
    if (!linkText || !linkHref || !text.includes(linkText)) return text;

    const [beforeLink, ...afterLinkParts] = text.split(linkText);
    return (
      <>
        {beforeLink}
        <a
          className="cc-course-document__link"
          href={linkHref}
          target="_blank"
          rel="noreferrer"
        >
          {linkText}
        </a>
        {afterLinkParts.join(linkText)}
      </>
    );
  };

  const renderBlocks = (blocks = []) => {
    const renderedBlocks = [];
    let bulletItems = [];

    const renderBulletList = (key) => {
      if (!bulletItems.length) return;
      const ordered = bulletItems.some(
        (item) => item.type === "ordered" || ORDERED_LIST_ITEMS.has(item.text)
      );
      const ListTag = ordered ? "ol" : "ul";
      renderedBlocks.push(
        <ListTag className="cc-course-document__list" key={key}>
          {bulletItems.map((item, index) => (
            <li key={`${item.text}-${index}`}>{item.text}</li>
          ))}
        </ListTag>
      );
      bulletItems = [];
    };

    blocks.forEach((block, index) => {
      if (isDocumentListBlock(block)) {
        bulletItems.push(block);
        return;
      }

      renderBulletList(`list-${index}`);
      renderedBlocks.push(
        block.type === "heading" ? (
          <h3 className="cc-course-document__heading" key={`${block.text}-${index}`}>
            {renderDocumentText(block)}
          </h3>
        ) : (
          <p key={`${block.text}-${index}`}>{renderDocumentText(block)}</p>
        )
      );
    });

    renderBulletList("list-final");
    return renderedBlocks;
  };

  const activeBlocks =
    activeSection === "reading" && hasReading ? lesson.reading : lesson.transcript;
  const contentSections = splitDocumentSections(activeBlocks);
  return (
    <section
      className={`cc-course-document cc-course-document--lesson-${lesson.rank}`}
      aria-label={`${lesson.title} course content`}
    >
      <div className="cc-course-document__tabs" role="tablist" aria-label="Course material">
        {hasReading && (
          <button
            type="button"
            role="tab"
            aria-selected={activeSection === "reading"}
            className={`cc-course-document__tab ${
              activeSection === "reading" ? "is-active" : ""
            }`}
            onClick={() => setActiveSection("reading")}
          >
            Reading
          </button>
        )}
        <button
          type="button"
          role="tab"
          aria-selected={activeSection === "transcript"}
          className={`cc-course-document__tab ${
            activeSection === "transcript" ? "is-active" : ""
          }`}
          onClick={() => setActiveSection("transcript")}
        >
          Transcript
        </button>
      </div>

      <div className="cc-course-document__body" role="tabpanel">
        {contentSections.map((section, index) => (
          <div className="cc-course-document__section" key={`${activeSection}-${index}`}>
            {renderBlocks(section)}
          </div>
        ))}
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Quiz copy is frontend-owned and follows the May 25, 2026 CC course document.
// ─────────────────────────────────────────────────────────────────────────────

const QUIZ_SUBTITLE = "This short recap is here to help you review the key ideas in this section. It is not a test, and it will not affect your certificate or your ability to continue.";

const QUIZ_DATA = {
  1: {
    title: "Quiz",
    subtitle: QUIZ_SUBTITLE,
    questions: [
      {
        id: "q1",
        text: "Why is this credit counseling course required for many people?",
        options: [
          "A. To prove you have tried every option before bankruptcy is allowed",
          "B. To help people understand their options and meet a legal requirement before filing bankruptcy",
          "C. To determine whether your debts qualify to be discharged",
          "D. To create an official repayment plan for your creditors",
        ],
        correctIndex: 1,
        explanation: "This course exists to give you clear, practical education so you can make an informed decision about your finances. For anyone considering bankruptcy, completing a Pre-Filing Credit Counseling session is a legal requirement under the Bankruptcy Code. That said, taking the course doesn't mean that you have to file bankruptcy. Plenty of people take it simply to get a clearer picture of their budget and options.",
      },
      {
        id: "q2",
        text: "What are the two required parts of this credit counseling session for people who decide to file bankruptcy?",
        options: [
          "A. 1) Watching the videos and 2) submitting your tax returns",
          "B. 1) Watching the videos and 2) meeting with a bankruptcy judge",
          "C. 1) Watching the videos and doing the online budget analysis, and 2) having a counseling conversation",
          "D. 1) Watching the videos and 2) getting approval from your creditors",
        ],
        correctIndex: 2,
        explanation: "For people who file bankruptcy, the session has two requirements: first, watch the videos and complete the online budget analysis, which organizes your income, expenses, debts, and assets into one clear financial picture. Second, you will have a conversation with a trained credit counselor by phone or live chat. Completing both parts is what allows Evergreen to issue your certificate of completion, which is required by the bankruptcy courts should you decide to file bankruptcy.",
      },
      {
        id: "q3",
        text: "What can Evergreen's counselors do during your conversation?",
        options: [
          "A. Recommend the best option for your specific situation",
          "B. Explain the material in the course and help you think through your options",
          "C. Tell you whether you are likely to qualify for bankruptcy",
          "D. Negotiate with your creditors on your behalf",
        ],
        correctIndex: 1,
        explanation: "Your counselor is there to review your information with you, answer general questions about the course material, and help you weigh your options. What they can't do is give legal advice, tell you whether to file for bankruptcy, approve or deny a case, or guarantee any particular outcome. Those decisions rest with you, your attorney, and the court.",
      },
    ],
  },
  3: {
    title: "Quiz",
    subtitle: QUIZ_SUBTITLE,
    questions: [
      {
        id: "q1",
        text: "What is one danger of shame around money?",
        options: [
          "A. It quietly pushes you to spend more to feel better",
          "B. It keeps you focused only on past mistakes instead of solutions",
          "C. It can paralyze you and block you from taking helpful action",
          "D. It makes you afraid to share your full situation with people who could help",
        ],
        correctIndex: 2,
        explanation: "Shame can feel like it should push you toward action, but it often does the opposite. It can leave you stuck, avoiding bills, calls, and decisions because facing them feels overwhelming. That kind of paralysis tends to make financial problems worse over time. Recognizing shame for what it is allows you to set it aside and take the practical steps that actually help.",
      },
      {
        id: "q2",
        text: "What is the main purpose of this course?",
        options: [
          "A. To review and assess the financial decisions that led you here",
          "B. To guide you toward the option that works best for most people",
          "C. To prepare you to repay as much of your debt as possible",
          "D. To provide education and clarity so you can make calm, informed choices",
        ],
        correctIndex: 3,
        explanation: "The purpose of this course is education, not judgment or pressure. Its goal is to give you a clear understanding of your finances and your options so you can make calm, informed choices on your own terms. It does not exist to evaluate your past decisions, steer you toward any particular path, or demand that you repay everything in full. The decisions remain yours.",
      },
      {
        id: "q3",
        text: "How does Evergreen suggest you view past \"mistakes\"?",
        options: [
          "A. As opportunities to learn and turn regret into wisdom",
          "B. As private matters best kept to yourself",
          "C. As patterns that are difficult to change",
          "D. As a sign that you should be cautious about asking for help",
        ],
        correctIndex: 0,
        explanation: "Evergreen encourages you to see past financial missteps as opportunities to learn rather than as evidence of failure. Regret, looked at honestly, can become wisdom that guides better decisions going forward. Hiding from past mistakes or treating them as proof you should not ask for help only keeps you stuck. Taking this course is itself a step that turns reflection into forward progress.",
      },
    ],
  },
  4: {
    title: "Quiz",
    subtitle: QUIZ_SUBTITLE,
    questions: [
      {
        id: "q1",
        text: "What is the main goal of the budget analysis?",
        options: [
          "A. To decide whether you qualify for bankruptcy",
          "B. To see your full financial picture",
          "C. To produce an official document your creditors can review",
          "D. To estimate how much of your debt you will be able to repay",
        ],
        correctIndex: 1,
        explanation: "The budget analysis is about clarity. By entering your income, expenses, debts, and assets in one place, you create a complete picture of where your finances stand, which you and your counselor can then review together. It is not a tax calculation, it does not decide whether you qualify for bankruptcy, and it never gives creditors access to your accounts. Its only job is to help you see your situation clearly so you can make informed decisions.",
      },
      {
        id: "q2",
        text: "What should you do if you cannot find every document listed in the checklist?",
        options: [
          "A. Wait and restart the course once you have located everything",
          "B. Enter only the amounts you can fully document and leave the rest blank",
          "C. Use your best estimates based on what you know",
          "D. Move on and complete the budget analysis another time",
        ],
        correctIndex: 2,
        explanation: "You do not need every document to move forward. If something is missing, use your best estimate based on what you know. The goal is an honest picture of your finances, not perfection. There is no need to stop the course or skip the budget analysis while you hunt for paperwork. A thoughtful estimate is far more useful than a blank space, and you can always refine the numbers as you go.",
      },
      {
        id: "q3",
        text: "If you share finances with a spouse or partner, what does the course ask you to do?",
        options: [
          "A. Include information for the whole household",
          "B. List only your own income and expenses",
          "C. List only your partner's income and expenses",
          "D. Complete a separate budget analysis for each person",
        ],
        correctIndex: 0,
        explanation: "If you share finances with a spouse or partner, include both of your information so the analysis reflects your whole household. A budget that leaves out part of the household income or expenses gives an incomplete picture, which makes it harder for you and your counselor to review your situation accurately. The aim is to capture the full financial reality you are actually working with.",
      },
    ],
  },
  5: {
    title: "Quiz",
    subtitle: QUIZ_SUBTITLE,
    questions: [
      {
        id: "q1",
        text: "Which of the following is an example of a secured debt?",
        options: [
          "A. Credit card balances",
          "B. Car loans",
          "C. Medical bills",
          "D. A personal loan with no collateral",
        ],
        correctIndex: 1,
        explanation: "A car loan is secured by the car itself - if payments stop, the lender can repossess it.",
      },
      {
        id: "q2",
        text: "If you fall behind on an unsecured debt, the lender can take a piece of your property to cover the debt.",
        options: [
          "A. True",
          "B. False",
        ],
        correctIndex: 1,
        explanation: "Unsecured debts aren't tied to collateral, so there's no specific property the lender can automatically take. Their options are things like collections or a lawsuit.",
      },
      {
        id: "q3",
        text: "What is the main difference between secured debt and unsecured debt?",
        options: [
          "A. Secured debts have higher interest rates than unsecured debts",
          "B. Secured debts are tied to specific property as collateral; unsecured debts are not",
          "C. Unsecured debts must always be paid back first",
          "D. Secured debts cannot appear on your credit report",
        ],
        correctIndex: 1,
        explanation: "A secured debt is backed by collateral the lender can claim; an unsecured debt is not.",
      },
    ],
  },
  8: {
    title: "Quiz",
    subtitle: QUIZ_SUBTITLE,
    questions: [
      {
        id: "q1",
        text: "What does debt consolidation do?",
        options: [
          "A. Combines several debts into one new loan",
          "B. Lowers the total amount you owe by reducing your balances",
          "C. Pauses your payments until your finances recover",
          "D. Replaces your debts with a court-ordered repayment plan smaller obligation.",
        ],
        correctIndex: 0,
        explanation: "Debt consolidation means rolling several debts into one new loan. You use that loan to pay off your older balances, and then you make a single payment to the new lender instead of juggling several. It is important to understand what consolidation does not do: it does not reduce or erase your debt, it does not pause your obligations, and it is not a court process. You still owe the full balance, just in a different package, so the goal is simpler payments, not a smaller obligation.",
      },
      {
        id: "q2",
        text: "For whom is debt consolidation most likely to work well?",
        options: [
          "A. Someone whose debt is overwhelming and no longer fits their budget",
          "B. Someone with steady income and at least fair credit, whose debt is large but still manageable",
          "C. Someone who has fallen far behind on most of their payments",
          "D. Someone with little or no income who needs immediate relief",
        ],
        correctIndex: 1,
        explanation: "Consolidation works best for people who have steady income and at least fair credit, because those are what qualify you for a reasonable interest rate. It is most effective when your debt is large but still manageable and the new payment genuinely fits your budget. When debt is overwhelming, income is tight, or you have already fallen far behind, consolidation tends to delay the problem rather than solve it, and another option may offer stronger relief.",
      },
      {
        id: "q3",
        text: "Which statement best describes a risk or limit of debt consolidation?",
        options: [
          "A. It may cost more over time if you stretch payments over extra years, and it offers no legal protection from lawsuits",
          "B. It guarantees a lower total interest cost but cannot reduce your monthly payment",
          "C. It protects you from lawsuits and garnishments through the automatic stay",
          "D. It prevents you from opening any new credit until the loan is fully repaid",
        ],
        correctIndex: 0,
        explanation: "Consolidation does not erase your debt; it repackages it. A lower monthly payment can feel like relief, but if you reach it by stretching the loan over more years, the total interest you pay can be much higher. Consolidation also offers no legal protection. If you default on the new loan, creditors can still sue you or pursue garnishment, just as they could before. That lack of protection is one of the key differences between consolidation and an option like bankruptcy, and it is worth discussing with an attorney.",
      },
    ],
  },
  9: {
    title: "Quiz",
    subtitle: QUIZ_SUBTITLE,
    questions: [
      {
        id: "q1",
        text: "What is the goal of debt settlement?",
        options: [
          "A. To reduce what you owe by negotiating lower payoff amounts with creditors",
          "B. To combine your debts into one new loan with a single monthly payment",
          "C. To lower your interest rates while keeping the full balance in place",
          "D. To set up a structured repayment plan managed by a counseling agency",
        ],
        correctIndex: 0,
        explanation: "The goal of debt settlement is to reduce your balances by negotiating with creditors, who may agree to accept less than the full amount as payoff. This is different from consolidation, which combines debts into one new loan without lowering what you owe, and different from a debt management plan, which restructures repayment rather than reducing the balance. Settlement aims at a smaller total, but that can come with real tradeoffs: settlement companies often charge high fees, your accounts usually fall further past due during negotiations and can harm your credit, there is no guarantee creditors will agree, and any debt that is forgiven may count as taxable income.",
      },
      {
        id: "q2",
        text: "What is one cost or risk of using a debt settlement company?",
        options: [
          "A. They charge no fees, since they are paid only by the creditors",
          "B. They often charge high fees, and accounts can fall further past due during negotiations",
          "C. They are able to settle every account you enroll, usually within a few weeks",
          "D. They protect your accounts from late fees and interest while they negotiate",
        ],
        correctIndex: 1,
        explanation: "Debt settlement companies charge significant fees, frequently based on the total amount of debt you enroll, so they do not work for free. On top of that, settlement usually involves stopping payments while negotiations happen, which means your accounts fall further past due and your credit can be harmed in the meantime. They cannot guarantee that every creditor will agree to settle, and they do not shield your accounts from the late fees or interest that continue to build. Understanding these costs is important before deciding whether settlement is worth it for you.",
      },
      {
        id: "q3",
        text: "How can forgiven debt affect your taxes?",
        options: [
          "A. It may be treated as taxable income, depending on your situation",
          "B. It has no effect on your taxes in any circumstance",
          "C. It is generally treated as tax-free because it is not money you received",
          "D. It reduces your overall tax bill by the amount that was forgiven",
        ],
        correctIndex: 0,
        explanation: "When a creditor forgives part of a debt, the forgiven amount may be treated as taxable income, depending on your situation. That means a settlement could leave you owing taxes on the portion that was written off. It is not automatically tax-free, and it does not lower your tax bill. Because the rules here depend on your individual circumstances, this is a good question to raise with a tax professional or attorney before you settle.",
      },
    ],
  },
  10: {
    title: "Quiz",
    subtitle: QUIZ_SUBTITLE,
    questions: [
      {
        id: "q1",
        text: "What does a debt management plan do?",
        options: [
          "A. Combines several debts into one new loan",
          "B. Restructures repayment of your unsecured debts through a credit counseling agency",
          "C. Lowers the total amount you owe by reducing your balances",
          "D. Replaces your debts with a court-ordered repayment plan",
        ],
        correctIndex: 1,
        explanation: "A debt management plan restructures how you repay your unsecured debts, usually credit cards. A nonprofit credit counseling agency contacts your creditors and asks them to lower interest rates or waive certain fees. If they agree, you make one monthly payment to the agency, and the agency distributes that money to your creditors. It is important to understand what a DMP does not do: it does not give you a new loan, since the agency distributes money you send rather than lending you any, and it does not reduce the balance you owe, which is debt settlement. It is also not a court process. You still repay what you owe in full, just under a simpler structure with potentially lower interest.",
      },
      {
        id: "q2",
        text: "For whom is a debt management plan most likely to work well?",
        options: [
          "A. Someone facing lawsuits, garnishment, or a long-term loss of income",
          "B. Someone whose main obstacle is high interest, with income steady enough to support the monthly payment",
          "C. Someone whose debt has grown larger than their budget can realistically handle",
          "D. Someone with little or no income who needs immediate relief",
        ],
        correctIndex: 1,
        explanation: "A debt management plan solves one specific problem well: high interest on unsecured debts. It works best when your income is steady and reliable, because the plan requires every payment to be made in full and on time, and missing payments can cause the plan to fail. When the real issue is something else (such as lawsuits or garnishment threats, a long-term loss of income, medical bills, or debt that has simply outgrown your budget) a DMP tends to delay the problem rather than solve it. Those situations usually call for an option with stronger protection, which is worth discussing before committing to a plan.",
      },
      {
        id: "q3",
        text: "Which statement best describes a risk or limit of a debt management plan?",
        options: [
          "A. Not all creditors will agree to participate, and the plan offers no legal protection from lawsuits",
          "B. It guarantees lower interest rates on every account you owe",
          "C. It protects you from lawsuits and garnishments while you are enrolled",
          "D. It erases a portion of your debt once you complete the program",
        ],
        correctIndex: 0,
        explanation: "A debt management plan depends entirely on creditor cooperation, and not every creditor will agree to participate. When some opt out, you may still have separate bills to manage outside the program, which reduces the simplicity the plan is meant to provide. A DMP also offers no legal protection. If you fall behind, creditors can sue you or pursue garnishment, just as they could before. Lower interest is only a possibility, never a guarantee, and the plan repays your full balance rather than erasing any of it. Other tradeoffs to weigh include setup and monthly fees charged by many agencies, and a note on your credit report indicating that you are enrolled in a debt management program.",
      },
    ],
  },
  11: {
    title: "Quiz",
    subtitle: QUIZ_SUBTITLE,
    questions: [
      {
        id: "q1",
        text: "What is the automatic stay in bankruptcy?",
        options: [
          "A. A waiting period that delays your case for three to six months before it begins",
          "B. A court order that stops most collection activity once a bankruptcy case is filed.",
          "C. A rule that pauses your repayment plan if you miss a monthly payment",
          "D. An agreement from creditors to lower your interest rates while your case is open",
        ],
        correctIndex: 1,
        explanation: "The automatic stay is a court order that usually takes effect the moment a bankruptcy case is filed, and it stops most collection activity against you. In many cases this includes lawsuits, wage garnishments, foreclosures and repossessions already in progress, and most collection calls and letters. Creditors are legally required to respect it. This legal protection is one of the most powerful features of bankruptcy, and it is a key difference from debt consolidation and debt settlement, neither of which can shield you from lawsuits or garnishment. The stay is automatic and enforced by the court.",
      },
      {
        id: "q2",
        text: "How does bankruptcy affect your credit, and what can recovery look like?",
        options: [
          "A. It permanently prevents you from qualifying for credit again",
          "B. It stays on your credit report for seven to ten years, but many people rebuild strong scores within one to two years.",
          "C. It is removed from your credit report as soon as your case is discharged",
          "D. It lowers your credit score but never appears on your credit report",
        ],
        correctIndex: 1,
        explanation: "A bankruptcy filing stays on your credit report for seven to ten years. But your credit report and your credit score are not the same thing: the report lists your accounts and payment history, while the score is the three-digit number lenders use to evaluate you. Even with a bankruptcy on the report, many people rebuild to strong scores, sometimes 720 or higher, within 12 to 24 months, depending on their circumstances and financial behavior. Many also receive new credit offers within a year of a Chapter 7 discharge.",
      },
      {
        id: "q3",
        text: "A common fear is that filing bankruptcy means losing everything you own. Is that accurate?",
        options: [
          "A. Yes, all property must be sold to repay creditors",
          "B. No, federal and state exemptions allow many people to keep basic property such as household goods and retirement accounts",
          "C. Yes, unless you file under Chapter 13 instead of Chapter 7",
          "D. No, because bankruptcy has no effect on property at all",
        ],
        correctIndex: 1,
        explanation: "The fear of losing everything is one of the most common myths about bankruptcy. In reality, federal and state laws provide exemptions that allow many people to keep basic property, such as household goods, retirement accounts, and in many cases a car or some home equity. The specific protections depend on your situation and the laws in your state. Because exemptions vary and can be complex, only a licensed attorney can explain how these rules would apply to your individual case.",
      },
    ],
  },
  "5-debts": {
    title: "Quiz",
    subtitle: "This short recap is here to help you review the difference between secured and unsecured debt. It is not a test, and it will not affect your certificate or your ability to continue. Understanding the difference will help as you navigate the rest of this course.",
    questions: [
      {
        id: "q1",
        text: "Which of the following is an example of a secured debt?",
        options: [
          "Credit card balances",
          "Car loans",
          "Medical bills",
          "A personal loan with no collateral",
        ],
        correctIndex: 1,
        explanation: "A car loan is secured by the car itself - if payments stop, the lender can repossess it.",
      },
      {
        id: "q2",
        text: "If you fall behind on an unsecured debt, the lender can take a piece of your property to cover the debt.",
        options: ["True", "False"],
        correctIndex: 1,
        explanation: "Unsecured debts are not tied to collateral, so there is no specific property the lender can automatically take. Their options are things like collections or a lawsuit.",
      },
      {
        id: "q3",
        text: "What is the main difference between secured debt and unsecured debt?",
        options: [
          "Secured debts have higher interest rates than unsecured debts",
          "Secured debts are tied to specific property as collateral; unsecured debts are not",
          "Unsecured debts must always be paid back first",
          "Secured debts cannot appear on your credit report",
        ],
        correctIndex: 1,
        explanation: "A secured debt is backed by collateral the lender can claim; an unsecured debt is not.",
      },
    ],
  },
  13: {
    title: "Quiz",
    subtitle: QUIZ_SUBTITLE,
    questions: [
      {
        id: "q1",
        text: "What does a credit score measure?",
        options: [
          "A. How likely you are to repay borrowed money, based on how you have used credit",
          "B. How responsible or trustworthy you are as a person",
          "C. How much money you currently have in savings and checking",
          "D. How much total income you earn each year",
        ],
        correctIndex: 0,
        explanation: "A credit score is a three-digit number that represents how likely you are to repay borrowed money. Lenders use it to decide whether to approve applications, what interest rate to offer, and how much risk they are taking. It is not a measure of your worth, intelligence, or character, and it is not based on your income or savings. It is simply a mathematical formula based on how you have used credit in the past, which means your future choices can change it.",
      },
      {
        id: "q2",
        text: "What is credit utilization, and what is the general goal?",
        options: [
          "A. The number of different lenders you have borrowed from, with more being better",
          "B. How much of your available credit you are using, with a goal of keeping it low",
          "C. How quickly you pay off a loan, with faster always being better",
          "D. How many years your oldest account has been open, with longer being better",
        ],
        correctIndex: 1,
        explanation: "Credit utilization refers to how much of your available credit you are using. A key step in rebuilding is keeping utilization low, generally in the range of 10 to 30 percent on each card and across all cards combined. Paying balances down before they report to the credit bureaus can help.",
      },
      {
        id: "q3",
        text: "Why can credit scores often recover within one to two years after bankruptcy?",
        options: [
          "A. Because the bankruptcy is removed from the credit report after one year",
          "B. Because lenders are required to ignore a bankruptcy when calculating a score",
          "C. Because the credit report and the credit score are not the same thing, and new habits can rebuild the score",
          "D. Because scores automatically reset to a high number once a case is discharged",
        ],
        correctIndex: 2,
        explanation: "A bankruptcy appears on your credit report for seven to ten years, but your credit report and your credit score are not the same thing. The report shows your history, while the score reflects your current behavior. Once old debts are discharged, your report shows fewer delinquent accounts, and you can begin adding positive information through on-time payments, low utilization, and responsible management of new accounts. This is why many people reach a 720 score within 12 to 24 months after completing bankruptcy.",
      },
    ],
  },
};
export const QuizView = ({ rank, quizKey = rank, lessonTitle, totalLessons, onSubmit, onBack, onSaveLater }) => {
  const quiz = QUIZ_DATA[quizKey];
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [attempted, setAttempted] = useState(false);

  if (!quiz) return null;

  const handleSelect = (questionId, optionIndex) => {
    if (submitted) return;
    setAnswers((prev) => ({ ...prev, [questionId]: optionIndex }));
  };

  const allAnswered = quiz.questions.every((q) => answers[q.id] !== undefined);

  const handleSubmit = () => {
    if (!allAnswered) {
      setAttempted(true);
      return;
    }
    setSubmitted(true);
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  const optionLetter = (index) => ["A", "B", "C", "D"][index];

  const buildAnswersPayload = () => Object.fromEntries(
    quiz.questions.map((question) => {
      const selectedIndex = answers[question.id];
      return [
        question.id,
        {
          question: question.text,
          options: question.options,
          selectedIndex,
          selectedAnswer: question.options[selectedIndex],
          correctIndex: question.correctIndex,
          correctAnswer: question.options[question.correctIndex],
          isCorrect: selectedIndex === question.correctIndex,
        },
      ];
    })
  );

  const getOptionStyle = (question, optIndex) => {
    const selected = answers[question.id] === optIndex;
    const isCorrect = question.correctIndex === optIndex;

    if (!submitted) {
      return {
        cursor: "pointer",
        borderColor: selected ? "#4E9C4B" : "transparent",
        background: selected ? "#f0f9f0" : "transparent",
      };
    }

    // After submit
    if (isCorrect) {
      return {
        borderColor: "#4E9C4B",
        background: "#f0f9f0",
      };
    }
    if (selected && !isCorrect) {
      return {
        borderColor: "#dc3545",
        background: "#fff5f5",
      };
    }
    return {
      background: "transparent",
    };
  };

  const getRadioColor = (question, optIndex) => {
    if (!submitted) return answers[question.id] === optIndex ? "#4E9C4B" : "#aaa";
    if (question.correctIndex === optIndex) return "#4E9C4B";
    if (answers[question.id] === optIndex) return "#dc3545";
    return "#aaa";
  };

  return (
    <div className="cc-quiz">
      {/*
        Google Translate replaces React-owned text nodes while translating the
        quiz.  Replace the complete quiz state as one DOM node so React never
        has to reconcile or remove those translated child text nodes.
      */}
      <div key={submitted ? "results" : "questions"} className="cc-quiz__state">
      {/* Header */}
      {!submitted ? (
        <>
          <h4 className="cc-quiz__title">LET&rsquo;S RECAP: {lessonTitle}</h4>
          <p className="cc-quiz__intro">
            {quiz.subtitle}
          </p>
        </>
      ) : (
        <>
          <h4 className="cc-quiz__title">LET&rsquo;S RECAP: Results</h4>
          <p className="cc-quiz__meta">
            Class {rank} of {totalLessons} - Pre-Filing Credit Counseling
          </p>
        </>
      )}

      {/* Questions */}
      {quiz.questions.map((question, qIdx) => {
        const userAnswer = answers[question.id];

        return (
          <div className="cc-quiz-question" key={question.id}>
            <p className="cc-quiz-question__title">
              {qIdx + 1}. {question.text}
            </p>

            {question.options.map((opt, optIdx) => (
              <div
                key={optIdx}
                className="cc-quiz-option"
                style={getOptionStyle(question, optIdx)}
                onClick={() => handleSelect(question.id, optIdx)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleSelect(question.id, optIdx);
                  }
                }}
                role="radio"
                aria-checked={answers[question.id] === optIdx}
                tabIndex={submitted ? -1 : 0}
              >
                {/* Radio circle */}
                <div
                  className="cc-quiz-option__radio"
                  style={{
                    border: `2px solid ${getRadioColor(question, optIdx)}`,
                    background: submitted && question.correctIndex === optIdx ? "#4E9C4B" : submitted && answers[question.id] === optIdx && optIdx !== question.correctIndex ? "#dc3545" : "transparent",
                  }}
                >
                  {submitted && question.correctIndex === optIdx && (
                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#fff" }} />
                  )}
                  {!submitted && answers[question.id] === optIdx && (
                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#4E9C4B" }} />
                  )}
                  {submitted && answers[question.id] === optIdx && optIdx !== question.correctIndex && (
                    <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "#fff" }} />
                  )}
                </div>
                <span className="cc-quiz-option__label">{opt}</span>
              </div>
            ))}

            {/* Validation error */}
            {attempted && !submitted && userAnswer === undefined && (
              <p style={{ color: "#dc3545", fontSize: "15px", marginTop: "4px" }}>
                Please select an answer.
              </p>
            )}

            {/* Correct answer explanation */}
            {submitted && (
              <div
                style={{
                  background: "#f0f9f0",
                  border: "1px solid #c3e6cb",
                  borderRadius: "6px",
                  padding: "12px 14px",
                  marginTop: "8px",
                }}
              >
                <p style={{ color: "#4E9C4B", fontWeight: 600, fontSize: "15px", marginBottom: "6px" }}>
                  Correct answer: {optionLetter(question.correctIndex)}
                </p>
                <p style={{ fontSize: "15px", color: "#333", margin: 0, lineHeight: 1.6 }}>
                  {question.explanation}
                </p>
              </div>
            )}
          </div>
        );
      })}

      {/* Footer Buttons */}
      <div className="footer-form-btn d-flex flex-wrap  mt-4">
        <button
          type="button"
          className="btn_brnd cc-action-back mt-4 me-3"
          onClick={onBack}
        >
          Go Back
        </button>

        <button
          type="button"
          className="btn_brnd cc-action-save-later mt-4 me-3"
          onClick={() => onSaveLater(submitted ? buildAnswersPayload() : null)}
        >
          Save and Return Later
        </button>

        {!submitted ? (
          <button
            type="button"
            className="btn_brnd mt-4 btn-next"
            onClick={handleSubmit}
          >
            Submit
          </button>
        ) : (
          <button
            type="button"
            className="btn_brnd mt-4 btn-next"
            onClick={() => {
              window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
              onSubmit(buildAnswersPayload());
            }}
          >
            {rank === totalLessons ? "Complete Course" : "Save and Continue"}
          </button>
        )}
      </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// REQUIRED FIELDS CONFIG PER FORM
// ─────────────────────────────────────────────────────────────────────────────

const REQUIRED_FIELDS = {
  budget_household: [],
  monthly_income: [],
  monthly_expenses: [],
  debts: [],
  legal_actions: [],
  assets: [],
  how_got_here: ["behaviors", "emotions", "response", "nextStep"],
  comparison: ["q1", "q2", "q3", "q4"],
};

const isRequired = (formName, fieldKey) =>
  (REQUIRED_FIELDS[formName] || []).includes(fieldKey);

const req = (formName, key, schema, msg) =>
  isRequired(formName, key) ? schema.required(msg) : schema.notRequired();

const optionalAmount = () =>
  Yup.number()
    .transform((value, originalValue) => originalValue === "" ? null : value)
    .nullable()
    .min(0, "Must be 0 or more")
    .typeError("Enter a valid amount")
    .notRequired();

const INCOME_FIELDS = [
  "Gross monthly income from work (before taxes)",
  "Net monthly income from work (take-home, after taxes)",
  "Self-employment income (net after business expenses)",
  "Social Security income",
  "Disability income",
  "Pension or retirement income",
  "Unemployment income",
  "Child support received",
  "Alimony or spousal support received",
  "Rental income",
  "Other regular income",
];

const buildMonthlyIncomeSchema = () => {
  const shape = {};
  INCOME_FIELDS.forEach((f) => {
    shape[f] = optionalAmount();
  });
  return Yup.object().shape(shape);
};

const EXPENSE_SECTIONS = [
  {
    label: "Housing and Utilities",
    fields: [
      "Rent or mortgage payment",
      "Property taxes (if not included in mortgage)",
      "Homeowners or renters insurance",
      "Homeowners association dues (HOA)",
      "Electricity",
      "Gas or heating fuel",
      "Water and sewer",
      "Trash and recycling",
      "Phone (cell or landline)",
      "Internet",
      "Other utilities (list and amount)",
    ],
  },
  {
    label: "Food and Household",
    fields: [
      "Groceries and household supplies",
      "Eating out and take-out",
      "Cleaning supplies and personal care",
    ],
  },
  {
    label: "Transportation",
    fields: [
      "Gas and fuel",
      "Public transportation, ride-share, or taxis",
      "Vehicle maintenance and repairs",
      "Vehicle registration and smog",
      "Car insurance",
      "Parking and tolls",
    ],
  },
  {
    label: "Insurance and Health",
    fields: [
      "Health insurance (if not taken out of paycheck)",
      "Out-of-pocket medical costs (doctor visits, prescriptions, etc.)",
      "Dental and vision expenses",
      "Life insurance",
      "Disability insurance",
      "Other insurance (list and amount)",
    ],
  },
  {
    label: "Children and Family",
    fields: [
      "Childcare or daycare",
      "School expenses and supplies",
      "Child support paid",
      "Alimony or spousal support paid",
      "Activities and lessons",
    ],
  },
  {
    label: "Other Regular Expenses",
    fields: [
      "Clothing and shoes",
      "Entertainment and streaming services",
      "Gym or memberships",
      "Charitable giving",
      "Pet expenses",
      "Other regular expenses (list and amount)",
    ],
  },
];

const EXPENSE_STEPS = [
  { sections: [0] },
  { sections: [1, 2] },
  { sections: [3] },
  { sections: [4] },
  { sections: [5] },
];

const EXPENSE_LIST_AMOUNT_FIELDS = new Set([
  "Other utilities (list and amount)",
  "Other insurance (list and amount)",
  "Other regular expenses (list and amount)",
]);

const expenseDescriptionField = (field) => field.replace("(list and amount)", "(description)");

const buildBudgetHouseholdSchema = () =>
  Yup.object().shape({
    who: Yup.string().notRequired(),
    householdSize: Yup.number()
      .transform((value, originalValue) => originalValue === "" ? null : value)
      .nullable()
      .min(1, "Must be at least 1")
      .typeError("Enter a valid number")
      .notRequired(),
    dependents: Yup.string().notRequired(),
  });

const buildMonthlyExpensesSchema = () => {
  const shape = { ownsHouse: Yup.string().oneOf(["", "yes", "no"]).notRequired() };
  EXPENSE_SECTIONS.forEach((sec) =>
    sec.fields.forEach((f) => {
      shape[f] = optionalAmount();
      if (EXPENSE_LIST_AMOUNT_FIELDS.has(f)) {
        shape[expenseDescriptionField(f)] = Yup.string().notRequired();
      }
    })
  );
  return Yup.object().shape(shape);
};

const buildDebtsSchema = () =>
  Yup.object().shape({
    studentLoanBalanceRange: Yup.string().oneOf(['zero', 'up_to_10000', '10001_to_20000', '20001_to_30000', 'over_30000'], 'Please select a student loan balance range').required('Please select a student loan balance range'),
    studentLoanAge: Yup.number()
      .transform((value, original) => original === '' ? undefined : value)
      .typeError('Please enter a valid age')
      .integer('Enter a whole-number age')
      .min(1, 'Enter an age between 1 and 120')
      .max(120, 'Enter an age between 1 and 120')
      .nullable()
      .notRequired(),
    oweTaxes: Yup.string().notRequired(),
    hasSolarPanels: Yup.string().oneOf(['', 'yes', 'no'], 'Please select Yes or No').notRequired(),
    hasOutstandingSolarLoan: Yup.string().oneOf(['', 'yes', 'no'], 'Please select Yes or No').notRequired(),
    solarLoanAmount: Yup.number()
      .transform((value, original) => original === '' ? undefined : value)
      .typeError('Enter a valid outstanding loan amount')
      .moreThan(0, 'Enter an amount greater than zero')
      .max(9999999999.99)
      .nullable()
      .notRequired(),
    taxTotal: optionalAmount(),
    taxPayment: optionalAmount(),
    secured: Yup.array().notRequired(),
    unsecured: Yup.array().notRequired(),
  });

const buildLegalActionsSchema = () =>
  Yup.object().shape({
    selected: isRequired("legal_actions", "selected")
      ? Yup.array().min(1, "Please select at least one option").required()
      : Yup.array().notRequired(),
  });

const buildAssetsSchema = () =>
  Yup.object().shape({
    "Cash on hand": optionalAmount(),
    "Checking account balances (total of all checking accounts)": optionalAmount(),
    "Savings account balances (total of all savings accounts)": optionalAmount(),
    "Retirement accounts (401(k), 403(b), IRA, pension, etc.)": optionalAmount(),
    "Other investments (stocks, bonds, mutual funds, crypto, etc.)": optionalAmount(),
    ownsRE: Yup.string().notRequired(),
    vehicles: Yup.array().notRequired(),
    realEstate: Yup.array().notRequired(),
    otherAssets: Yup.array().notRequired(),
  });

const VEHICLE_TYPES = ["Car", "Truck", "Motorcycle", "RV", "Other"];
const PROPERTY_TYPES = ["Primary residence", "Rental", "Land", "Other"];

const createVehicleRow = () => ({ type: "", model: "", value: "", owed: "" });
const createRealEstateRow = () => ({ type: "", value: "", owed: "" });
const createOtherAssetRow = () => ({
  businessDescription: "",
  businessValue: "",
  otherDescription: "",
  otherValue: "",
});

const RETIREMENT_ASSET_FIELD = "Retirement accounts (401(k), 403(b), IRA, pension, etc.)";
const INVESTMENT_ASSET_FIELD = "Other investments (stocks, bonds, mutual funds, crypto, etc.)";

const hasVehicleEntryData = (vehicle = {}) => Boolean(
  vehicle.type || vehicle.model || vehicle.value || vehicle.owed
);
const hasPropertyEntryData = (property = {}) => Boolean(
  property.type || property.value || property.owed
);
const hasOtherAssetEntryData = (asset = {}) => Boolean(
  asset.businessDescription || asset.businessValue || asset.otherDescription || asset.otherValue
);

const rowsWithPendingEditor = (rows, editor, hasData, keyFields = [], editingIndex) => {
  const preparedRows = (Array.isArray(rows) ? rows : [])
    .filter(hasData)
    .map((row) => ({ ...row }));
  if (!hasData(editor)) return preparedRows;

  const matchingIndex = Number.isInteger(editingIndex) && editingIndex >= 0 && editingIndex < preparedRows.length
    ? editingIndex
    : preparedRows.findIndex((row) => keyFields.some(
      (key) => editor?.[key] && row?.[key] === editor[key]
    ));
  if (matchingIndex >= 0) preparedRows[matchingIndex] = { ...editor };
  else preparedRows.push({ ...editor });
  return preparedRows;
};

const readFormikLiteralField = (values, label) => {
  if (values[label] !== undefined && values[label] !== "") return values[label];
  const dotIndex = label.lastIndexOf(".");
  if (dotIndex < 0) return values[label];
  const brokenRoot = label.slice(0, dotIndex);
  const brokenLeaf = label.slice(dotIndex + 1);
  return values[brokenRoot]?.[brokenLeaf] ?? values[label];
};

const SECURED_DEBT_TYPES = [
  "Mortgage",
  "Home equity loan or line of credit",
  "Car or truck loan",
  "RV or motorcycle",
  "Furniture or appliance loan",
  "Other secured debt",
];

const UNSECURED_DEBT_TYPES = [
  "Credit card",
  "Personal loan",
  "Medical bill",
  "Retail store credit card",
  "Payday or cash advance",
  "Collections",
  "Other unsecured",
];

const normalizeDebtTypeLabel = (type) =>
  type === "Store card" ? "Retail store credit card" : type;

const createDebtRow = () => ({
  creditor: "",
  type: "",
  balance: "",
  payment: "",
  monthsBehind: "0",
});

const hasDebtEntryData = (debt = {}) => Boolean(
  debt.creditor || debt.type || debt.balance || debt.payment ||
  (debt.monthsBehind !== undefined && debt.monthsBehind !== "" && debt.monthsBehind !== "0")
);

const prepareClassFiveFormValues = (formName, values = {}) => {
  values = values || {};
  const prepared = { ...values };
  if (formName === "monthly_income") {
    delete prepared.who;
    delete prepared.householdSize;
    delete prepared.dependents;
  }

  if (formName === "monthly_income" && values.person2) {
    INCOME_FIELDS.forEach((field) => {
      const amounts = [values[field], values.person2[field]];
      prepared[field] = amounts.every((amount) => amount === "" || amount == null)
        ? "" : Math.round(amounts.reduce((total, amount) => total + (Number(amount) || 0), 0) * 100) / 100;
    });
    prepared.person1OtherDescription = [...new Set([
      values.person1OtherDescription, values.person2OtherDescription,
    ].filter(Boolean))].join("; ");
    delete prepared.person2;
    delete prepared.person2OtherDescription;
  }

  if (formName === "monthly_expenses") {
    prepared.ownsHouse = values.ownsHouse || "yes";
  }

  if (formName === "debts") {
    prepared.studentLoanBalanceRange = values.studentLoanBalanceRange || "zero";
    prepared.hasSolarPanels = values.hasSolarPanels || "no";
    ["secured", "unsecured"].forEach((arrayKey) => {
      const draftKey = `${arrayKey}Draft`;
      prepared[arrayKey] = rowsWithPendingEditor(
        values[arrayKey], values[draftKey], hasDebtEntryData, ["legacyId"], values[draftKey]?.editingIndex
      ).map(({ editingIndex, ...debt }) => ({ ...debt, type: normalizeDebtTypeLabel(debt.type) }));
      delete prepared[draftKey];
    });
  }

  if (formName === "assets") {
    prepared[RETIREMENT_ASSET_FIELD] = readFormikLiteralField(values, RETIREMENT_ASSET_FIELD);
    prepared[INVESTMENT_ASSET_FIELD] = readFormikLiteralField(values, INVESTMENT_ASSET_FIELD);
    [RETIREMENT_ASSET_FIELD, INVESTMENT_ASSET_FIELD].forEach((label) => {
      const dotIndex = label.lastIndexOf(".");
      delete prepared[label.slice(0, dotIndex)];
    });
    prepared.vehicles = rowsWithPendingEditor(
      values.vehicles, values.vehicleDraft, hasVehicleEntryData, ["itemKey"]
    );
    prepared.realEstate = rowsWithPendingEditor(
      values.realEstate, values.propertyDraft, hasPropertyEntryData, ["itemKey"]
    );
    prepared.otherAssets = rowsWithPendingEditor(
      values.otherAssets, values.otherAssetDraft, hasOtherAssetEntryData,
      ["businessItemKey", "otherItemKey"]
    );
    delete prepared.vehicleDraft;
    delete prepared.propertyDraft;
    delete prepared.otherAssetDraft;
  }

  return prepared;
};

const buildReviewConfirmSchema = () =>
  Yup.object().shape({
    confirmed: Yup.boolean().notRequired(),
  });

const buildHowGotHereSchema = () =>
  Yup.object().shape({
    behaviors: req("how_got_here", "behaviors", Yup.string(), "This field is required"),
    emotions: req("how_got_here", "emotions", Yup.string(), "This field is required"),
    response: req("how_got_here", "response", Yup.string(), "This field is required"),
    nextStep: req("how_got_here", "nextStep", Yup.string(), "This field is required"),
  });

const buildComparisonSchema = () =>
  Yup.object().shape({
    q1: req("comparison", "q1", Yup.string(), "Please select an option"),
    q2: req("comparison", "q2", Yup.string(), "Please select an option"),
    q3: req("comparison", "q3", Yup.string(), "Please select an option"),
    q4: req("comparison", "q4", Yup.string(), "Please select an option"),
    attorney: req("comparison", "attorney", Yup.string(), "Please select an option"),
  });

const SCHEMA_MAP = {
  budget_household: buildBudgetHouseholdSchema,
  monthly_income: buildMonthlyIncomeSchema,
  monthly_expenses: buildMonthlyExpensesSchema,
  debts: buildDebtsSchema,
  legal_actions: buildLegalActionsSchema,
  assets: buildAssetsSchema,
  review_confirm: buildReviewConfirmSchema,
  how_got_here: buildHowGotHereSchema,
  comparison: buildComparisonSchema,
};

const INITIAL_VALUES = {
  budget_household: { who: "", householdSize: "", dependents: "" },
  monthly_income: {
    ...Object.fromEntries(INCOME_FIELDS.map((f) => [f, ""])),
    person1OtherDescription: "",
  },
  monthly_expenses: { ownsHouse: "yes", ...Object.fromEntries(
    EXPENSE_SECTIONS.flatMap((s) => s.fields).flatMap((f) => (
      EXPENSE_LIST_AMOUNT_FIELDS.has(f)
        ? [[f, ""], [expenseDescriptionField(f), ""]]
        : [[f, ""]]
    ))
  ) },
  debts: {
    secured: [],
    studentLoanBalanceRange: 'zero',
    studentLoanAge: '',
    unsecured: [],
    securedDraft: createDebtRow(),
    unsecuredDraft: createDebtRow(),
    oweTaxes: "no",
    hasSolarPanels: "no",
    ownsHouse: "yes",
    hasOutstandingSolarLoan: "",
    solarLoanAmount: "",
    taxTotal: "",
    taxPayment: "",
  },
  legal_actions: { selected: [] },
  assets: {
    "Cash on hand": "",
    "Checking account balances (total of all checking accounts)": "",
    "Savings account balances (total of all savings accounts)": "",
    [RETIREMENT_ASSET_FIELD]: "",
    [INVESTMENT_ASSET_FIELD]: "",
    vehicles: [],
    vehicleDraft: createVehicleRow(),
    ownsRE: "no",
    realEstate: [],
    propertyDraft: createRealEstateRow(),
    otherAssets: [],
    otherAssetDraft: createOtherAssetRow(),
  },
  review_confirm: { confirmed: false },
  how_got_here: {
    lifeEvents: [],
    behavioral: [],
    practical: [],
    behaviors: "",
    emotions: "",
    response: "",
    nextStep: "",
  },
  comparison: { q1: "", q2: "", q3: "", q4: "", attorney: "" },
};

// ─────────────────────────────────────────────────────────────────────────────
// FORM FIELD COMPONENTS (unchanged from original)
// ─────────────────────────────────────────────────────────────────────────────

const Req = ({ formName, fieldKey }) =>
  isRequired(formName, fieldKey) ? <span className="text-danger"> *</span> : null;

const BudgetFormGuidance = ({ children }) => (
  <div className="cc-budget-form-note cc-budget-form-guidance" role="note">
    <i className="fa-regular fa-circle-question" aria-hidden="true" />
    <strong>{children}</strong>
  </div>
);

const BudgetHouseholdForm = ({ values, setFieldValue, errors, touched }) => (
  <div className="mt-4 cc-household-form">
    <h5 className="mb-3" style={{ color: "#4E9C4B" }}>About Your Household</h5>
    <BudgetFormGuidance>Include everyone you financially support and anyone who shares your household finances. If you are unsure of a dependent's exact age, use your best estimate.</BudgetFormGuidance>
    <div className="mb-5">
      <label className="label-title">
        Who is included in this budget?
        <Req formName="budget_household" fieldKey="who" />
      </label>
      <div className="cc-household-choice-grid">
        {[
          {
            value: "Me only",
            label: "Me only",
            detail: "The budget includes only your income and expenses.",
          },
          {
            value: "Me and my spouse",
            label: "Me and my spouse",
            detail: "You and your spouse share household finances.",
          },
          {
            value: "Me and my partner or other household member who shares finances",
            label: "Shared household",
            detail: "A partner or another household member shares finances with you.",
          },
        ].map((option) => (
          <label
            className={`cc-household-choice${values.who === option.value ? " is-selected" : ""}`}
            htmlFor={`who_${option.value}`}
            key={option.value}
          >
            <Field
              className="cc-household-choice__input"
              type="radio"
              name="who"
              value={option.value}
              id={`who_${option.value}`}
            />
            <span className="cc-household-choice__mark" aria-hidden="true" />
            <span className="cc-household-choice__copy">
              <strong>{option.label}</strong>
              <small>{option.detail}</small>
            </span>
          </label>
        ))}
      </div>
      <small className="text-muted">
        Choose the option that best matches how you pay bills in real life. If you and another adult share income and expenses, include both of you.
      </small>
      <ErrorMessage name="who" component="div" className="error text-danger" />
    </div>
    <div className="row cc-household-form__field-row cc-household-form__field-row--number">
      <div className="col-12">
        <div className="input_wrap">
          <label className="label-title">
            Number of people in your household
            <Req formName="budget_household" fieldKey="householdSize" />
          </label>
          <Field type="number" min="1" name="householdSize" className="form-control" placeholder="e.g. 3" />
          <small className="text-muted">
            Include yourself, your spouse or partner if you share finances, and anyone you financially support who lives with you (such as children or other relatives).
          </small>
          <ErrorMessage name="householdSize" component="div" className="error text-danger" />
        </div>
      </div>
    </div>
    <div className="row cc-household-form__field-row">
      <div className="col-md-8">
        <div className="input_wrap">
          <label className="label-title">
            Dependents in the household
            <Req formName="budget_household" fieldKey="dependents" />
          </label>
          <Field type="text" name="dependents" className="form-control" placeholder="List the ages of any children or other dependents" />
          <small className="text-muted">This information gives your counselor context. It does not affect your certificate.</small>
          <ErrorMessage name="dependents" component="div" className="error text-danger" />
        </div>
      </div>
    </div>
  </div>
);

const MonthlyIncomeForm = () => {
  const renderHouseholdIncome = () => {
    const descriptionField = "person1OtherDescription";

    return (
      <section className="cc-budget-form-section">
        <h6 className="cc-budget-form-section__title">
          Your Monthly Household Income
        </h6>
        <div className="row">
          {INCOME_FIELDS.map((field) => {
            const fieldName = field;
            return (
              <div className="col-md-6 mb-3" key={fieldName}>
                <div className="input_wrap cc-money-field">
                  <label className="label-title">{field}</label>
                  {field === "Other regular income" ? (
                    <div className="row g-2">
                      <div className="col-6">
                        <Field name={descriptionField} className="form-control" placeholder="e.g. Freelancing" />
                      </div>
                      <div className="col-6">
                        <Field type="number" step="0.01" inputMode="decimal" min="0" name={fieldName} className="form-control" placeholder="Amount $ 0.00" />
                      </div>
                    </div>
                  ) : (
                    <Field type="number" step="0.01" inputMode="decimal" min="0" name={fieldName} className="form-control" placeholder="$ 0.00" />
                  )}
                </div>
                <ErrorMessage name={fieldName} component="div" className="error text-danger" />
              </div>
            );
          })}
        </div>
        {(
          <div className="cc-budget-form-note">
            <i className="fa-regular fa-circle-question" aria-hidden="true" />
            <span>
              If you are paid weekly or every two weeks, you can use an average. Add two or three recent paychecks together and divide by two or three.
            </span>
          </div>
        )}
      </section>
    );
  };

  return (
    <div className="mt-4 cc-income-form">
      <BudgetFormGuidance>If you are not sure of exact income amounts, use your best estimate. Enter monthly amounts. If your income changes, use an average from the last three to six months. As explained in the previous video, include yourself and anyone whose income or expenses are part of your household finances.</BudgetFormGuidance>
      {renderHouseholdIncome()}
    </div>
  );
};

const MonthlyExpensesForm = ({ activeStep = 1, errors, touched }) => {
  const sectionIndexes = EXPENSE_STEPS[activeStep - 1]?.sections || [0];
  const columnClass = activeStep === 2 || activeStep === 4 || activeStep === 5
    ? "col-lg-4 col-md-6"
    : "col-md-6";

  return (
    <div className="mt-4 cc-expenses-form">
      <BudgetFormGuidance>If you are not sure of exact expenses, use your best estimate of what your household spends each month. Convert occasional or yearly bills to a monthly average.</BudgetFormGuidance>
      <div className="cc-expense-stepper" aria-label={`Monthly expenses step ${activeStep} of 5`}>
        {[1, 2, 3, 4, 5].map((stepNumber) => (
          <div
            className={`cc-expense-stepper__step${stepNumber < activeStep ? " is-complete" : ""}${stepNumber === activeStep ? " is-active" : ""}`}
            key={stepNumber}
          >
            <span>{stepNumber}</span>
          </div>
        ))}
      </div>

      {sectionIndexes.map((sectionIndex) => {
        const section = EXPENSE_SECTIONS[sectionIndex];
        return (
          <section className="cc-expense-section" key={section.label}>
            <h6 className="cc-budget-form-section__title">{section.label}</h6>
            {sectionIndex === 0 && (
              <fieldset className="cc-solar-question mb-4">
                <legend className="label-title">Do you own or rent a home?</legend>
                <div className="cc-tax-debt-card__options">
                  {[["yes", "Own"], ["no", "Rent"]].map(([value, label]) => (
                    <label className="form-check" key={value}>
                      <Field className="form-check-input" type="radio" name="ownsHouse" value={value} />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
                <ErrorMessage name="ownsHouse" component="div" className="error text-danger" />
              </fieldset>
            )}
            <div className="row cc-expense-section__grid">
              {section.fields.map((field) => {
                const isListAndAmount = EXPENSE_LIST_AMOUNT_FIELDS.has(field);
                const fieldName = `["${field}"]`;
                return (
                  <div className={`${isListAndAmount ? "col-md-6" : columnClass} mb-3`} key={field}>
                    <div className="input_wrap cc-money-field">
                      <label className="label-title">{field}</label>
                      {isListAndAmount ? (
                        <div className="row g-2">
                          <div className="col-6">
                            <Field name={expenseDescriptionField(field)} className="form-control" placeholder="Description" />
                          </div>
                          <div className="col-6">
                            <Field type="number" step="0.01" inputMode="decimal" min="0" name={fieldName} className="form-control" placeholder="Amount $ 0.00" />
                          </div>
                        </div>
                      ) : (
                        <Field
                          type="number"
                          step="0.01"
                          inputMode="decimal"
                          min="0"
                          name={fieldName}
                          className={`form-control${errors[field] && touched[field] ? " is-invalid" : ""}`}
                          placeholder="$ 0.00"
                        />
                      )}
                      <ErrorMessage name={fieldName} component="div" className="error text-danger" />
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        );
      })}

      {activeStep === 1 && (
        <div className="cc-budget-form-note cc-expense-note">
          <i className="fa-regular fa-circle-question" aria-hidden="true" />
          <span>If your mortgage payment already includes taxes or insurance, do not list them separately again.</span>
        </div>
      )}
    </div>
  );
};

const DebtTypeSelect = ({ value, options, onChange, label }) => {
  const [open, setOpen] = useState(false);

  return (
    <div
      className={`cc-debt-select${open ? " is-open" : ""}`}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <button
        type="button"
        className="cc-debt-select__trigger"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={label}
      >
        <span className={value ? "" : "is-placeholder"}>{value || "Select"}</span>
        <i className="fa-solid fa-chevron-down" aria-hidden="true" />
      </button>
      {open && (
        <div className="cc-debt-select__menu" role="listbox" aria-label={label}>
          {options.map((option) => (
            <button
              type="button"
              role="option"
              aria-selected={option === value}
              className={`cc-debt-select__option${option === value ? " is-selected" : ""}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
              key={option}
            >
              <span>{option}</span>
              {option === value && <i className="fa-solid fa-check" aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const DebtsForm = ({ values, setFieldValue, errors, touched }) => {
  const hasDebtData = hasDebtEntryData;

  const getSavedRows = (arrayKey) => (values[arrayKey] || []).filter(hasDebtData);
  const getDraft = (arrayKey) => values[`${arrayKey}Draft`] || createDebtRow();
  const updateDraft = (arrayKey, field, value) => {
    setFieldValue(`${arrayKey}Draft.${field}`, value);
  };

  const saveDraft = (arrayKey) => {
    const draft = getDraft(arrayKey);
    if (!hasDebtData(draft)) return;

    const rows = getSavedRows(arrayKey);
    const editIndex = draft.editingIndex ?? null;
    const { editingIndex, ...savedDraft } = draft;
    const nextRows = [...rows];
    if (editIndex === null) nextRows.push(savedDraft);
    else nextRows[editIndex] = savedDraft;

    setFieldValue(arrayKey, nextRows);
    setFieldValue(`${arrayKey}Draft`, createDebtRow());
  };

  const editRow = (arrayKey, index) => {
    const debt = getSavedRows(arrayKey)[index];
    setFieldValue(`${arrayKey}Draft`, { ...debt, editingIndex: index });
  };

  const removeRow = (arrayKey, index) => {
    const rows = getSavedRows(arrayKey).filter((_, rowIndex) => rowIndex !== index);
    setFieldValue(arrayKey, rows);
    const editIndex = getDraft(arrayKey).editingIndex ?? null;
    if (editIndex === index) {
      setFieldValue(`${arrayKey}Draft`, createDebtRow());
    } else if (editIndex !== null && editIndex > index) {
      setFieldValue(`${arrayKey}Draft.editingIndex`, editIndex - 1);
    }
  };

  const cancelEdit = (arrayKey) => {
    setFieldValue(`${arrayKey}Draft`, createDebtRow());
  };

  const renderDebtSection = ({ arrayKey, heading, paymentLabel, types }) => {
    const rows = getSavedRows(arrayKey);
    const draft = getDraft(arrayKey);
    const editIndex = draft.editingIndex ?? null;
    const debtLabel = arrayKey === "secured" ? "secured debt" : "unsecured debt";
    const buttonLabel = editIndex !== null
      ? `Update ${debtLabel}`
      : rows.length > 0
        ? `Add another ${debtLabel}`
        : `Add ${debtLabel}`;

    return (
      <section className="cc-debt-card">
        <header className="cc-debt-card__header">
          <h6>{heading}</h6>
        </header>

        {rows.length > 0 && (
          <div className="cc-debt-summary-wrap">
            <table className="cc-debt-summary-table">
              <thead>
                <tr>
                  <th>Creditor</th>
                  <th>Type</th>
                  <th>Approx. balance</th>
                  <th>{paymentLabel}</th>
                  <th>Months behind</th>
                  <th><span className="visually-hidden">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((debt, index) => (
                  <tr key={`${arrayKey}-saved-${index}`}>
                    <td>{debt.creditor || "-"}</td>
                    <td>{debt.type || "-"}</td>
                    <td>{debt.balance ? `$${debt.balance}` : "-"}</td>
                    <td>{debt.payment ? `$${debt.payment}` : "-"}</td>
                    <td>{debt.monthsBehind || "0"}</td>
                    <td>
                      <div className="cc-debt-summary-table__actions">
                        <button type="button" className="cc-debt-row-action is-edit" onClick={() => editRow(arrayKey, index)} aria-label={`Edit ${debtLabel} ${index + 1}`}>
                          <i className="fa-solid fa-pen" aria-hidden="true" />
                        </button>
                        <button type="button" className="cc-debt-row-action is-delete" onClick={() => removeRow(arrayKey, index)} aria-label={`Remove ${debtLabel} ${index + 1}`}>
                          <i className="fa-solid fa-trash" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="cc-debt-editor">
          {editIndex !== null && (
            <div className="cc-debt-editor__status">
              <span>Editing {debtLabel} #{editIndex + 1}</span>
              <button type="button" onClick={() => cancelEdit(arrayKey)}>Cancel edit</button>
            </div>
          )}
          <div className="cc-debt-entry__grid">
            <div className="input_wrap">
              <label className="label-title">Creditor name</label>
              <input className="form-control" value={draft.creditor} onChange={(event) => updateDraft(arrayKey, "creditor", event.target.value)} placeholder="Name" />
            </div>
            <div className="input_wrap">
              <label className="label-title">Type</label>
              <DebtTypeSelect
                value={draft.type}
                options={types}
                onChange={(value) => updateDraft(arrayKey, "type", value)}
                label={`Select ${debtLabel} type`}
              />
            </div>
            <div className="input_wrap">
              <label className="label-title">Approximate balance</label>
              <input type="number" step="0.01" inputMode="decimal" min="0" className="form-control" value={draft.balance} onChange={(event) => updateDraft(arrayKey, "balance", event.target.value)} placeholder="$ 0.00" />
            </div>
            <div className="input_wrap">
              <label className="label-title">{paymentLabel}</label>
              <input type="number" step="0.01" inputMode="decimal" min="0" className="form-control" value={draft.payment} onChange={(event) => updateDraft(arrayKey, "payment", event.target.value)} placeholder="$ 0.00" />
            </div>
            <div className="input_wrap">
              <label className="label-title">Months behind (0 if current)</label>
              <input type="number" min="0" className="form-control" value={draft.monthsBehind} onChange={(event) => updateDraft(arrayKey, "monthsBehind", event.target.value)} placeholder="0" />
            </div>
          </div>
        </div>

        <button type="button" className="cc-debt-add" onClick={() => saveDraft(arrayKey)} disabled={!hasDebtData(draft)}>
          <i className={`fa-solid ${editIndex !== null ? "fa-check" : "fa-plus"}`} aria-hidden="true" />
          <span>{buttonLabel}</span>
        </button>
      </section>
    );
  };

  return (
    <div className="mt-4 cc-debts-form">
      <h5 className="mb-3" style={{ color: "#4E9C4B" }}>Debts</h5>
      <p className="cc-debts-form__intro">
        This section focuses on money you owe to creditors. You will list secured debts, such as mortgages or car loans, and unsecured debts, such as credit cards or medical bills.
      </p>

      <section className="cc-debt-definitions">
        <h6>Definitions</h6>
        <div>
          <strong>Secured debts</strong>
          <p>Secured debts are loans tied to a specific piece of property, called collateral. The property guarantees the loan. If you don't pay, the lender can take that property back. For instance, a mortgage is secured by your home; a car loan is secured by your car.</p>
        </div>
        <div>
          <strong>Unsecured debts</strong>
          <p>Unsecured debts aren't tied to any property. There's no collateral the lender can automatically take if you fall behind. Credit cards and medical bills are common examples. The lender gave you credit based on your promise to pay it back, without tying it to any of your property.</p>
        </div>
      </section>

      {renderDebtSection({
        arrayKey: "secured",
        heading: <>A - Secured <span>Debts</span></>,
        paymentLabel: "Regular monthly payment",
        types: SECURED_DEBT_TYPES,
      })}

      {renderDebtSection({
        arrayKey: "unsecured",
        heading: <>B - Unsecured <span>Debts</span> <small>(not tied to property)</small></>,
        paymentLabel: "Minimum or typical monthly payment",
        types: UNSECURED_DEBT_TYPES,
      })}

      <StudentLoanDebtSection />

      <section className="cc-debt-card cc-tax-debt-card">
        <header className="cc-debt-card__header">
          <h6>D - Tax <span>Debts</span></h6>
        </header>
        <div className="cc-tax-debt-card__question">
          <label className="label-title">Do you owe back taxes?</label>
          <div className="cc-tax-debt-card__options">
            <label className="form-check">
              <Field className="form-check-input" type="radio" name="oweTaxes" value="yes" />
              <span>Yes</span>
            </label>
            <label className="form-check">
              <Field className="form-check-input" type="radio" name="oweTaxes" value="no" />
              <span>No</span>
            </label>
          </div>
        </div>

        {values.oweTaxes === "yes" && (
          <div className="cc-tax-debt-card__fields">
            <div className="input_wrap">
              <label className="label-title">Approximate total tax owed</label>
              <Field type="number" step="0.01" inputMode="decimal" min="0" name="taxTotal" className={`form-control${errors.taxTotal && touched.taxTotal ? " is-invalid" : ""}`} placeholder="$ 0.00" />
              <ErrorMessage name="taxTotal" component="div" className="error text-danger" />
            </div>
            <div className="input_wrap">
              <label className="label-title">Monthly payment on any tax payment plan (if none, enter 0)</label>
              <Field type="number" step="0.01" inputMode="decimal" min="0" name="taxPayment" className={`form-control${errors.taxPayment && touched.taxPayment ? " is-invalid" : ""}`} placeholder="$ 0.00" />
              <ErrorMessage name="taxPayment" component="div" className="error text-danger" />
            </div>
          </div>
        )}

        <div className="cc-budget-form-note cc-tax-debt-card__note">
          <i className="fa-regular fa-circle-question" aria-hidden="true" />
          <span>Tax debts often follow different rules from other debts. Your counselor can discuss general educational information. For specific legal or tax advice, speak with an attorney or tax professional.</span>
        </div>
      </section>
      <SolarPanelsSection values={values} setFieldValue={setFieldValue} />
    </div>
  );
};

const LEGAL_OPTIONS = [
  "I have received a lawsuit or court summons about a debt.",
  "My wages are being garnished or I have received notice of garnishment.",
  "My bank account has been frozen or levied.",
  "My mortgage company or landlord has started or threatened foreclosure or eviction.",
  "A lender has repossessed or is threatening to repossess a vehicle or other property.",
  "Debt collectors contact me frequently by phone, mail, email, or text.",
  "None of the above.",
];

const legalOptionLabel = (option) => option === LEGAL_OPTIONS[0]
  ? `Yes, ${option}`
  : option;

const LegalActionsForm = ({ values, setFieldValue, errors, touched }) => {
  const toggle = (opt) => {
    const curr = values.selected || [];
    if (opt === "None of the above.") { setFieldValue("selected", ["None of the above."]); return; }
    const without = curr.filter((o) => o !== "None of the above." && o !== opt);
    if (curr.includes(opt)) setFieldValue("selected", without);
    else setFieldValue("selected", [...without, opt]);
  };
  return (
    <div className="mt-4 cc-legal-actions-form">
      <h5 className="mb-1" style={{ color: "#4E9C4B" }}>Legal Actions and Collection Activities</h5>
      <div className="cc-legal-actions-intro" role="note">
        <span className="cc-legal-actions-intro__icon" aria-hidden="true">
          <i className="fa-solid fa-shield-halved" />
        </span>
        <p>
          These questions help your counselor understand whether any of your debts have turned into
          legal actions. Your answers do not affect your certificate and are not used to make legal
          decisions.
        </p>
      </div>
      <fieldset className="cc-legal-actions-fieldset">
        <legend className="cc-legal-actions-prompt">
          <span className="cc-legal-actions-prompt__icon" aria-hidden="true">
            <i className="fa-solid fa-list-check" />
          </span>
          <span>
            Are you currently experiencing any of the following related to your debts? Select all that
            apply:<Req formName="legal_actions" fieldKey="selected" />
          </span>
        </legend>
        <div className="cc-legal-actions-options">
          {LEGAL_OPTIONS.map((opt) => (
            <div className="form-check mb-2" key={opt}>
              <input className="form-check-input" type="checkbox" checked={(values.selected || []).includes(opt)} onChange={() => toggle(opt)} id={"legal_" + opt} />
              <label className="form-check-label" htmlFor={"legal_" + opt}>{legalOptionLabel(opt)}</label>
            </div>
          ))}
        </div>
        {errors.selected && touched.selected && <div className="error text-danger">{errors.selected}</div>}
      </fieldset>
    </div>
  );
};

const SolarPanelsSection = ({ values }) => {
  const { setValues } = useFormikContext();
  const { hasSolarPanels } = values;

  return (
    <section className="cc-debt-card cc-solar-panel-card" aria-label="Solar panels">
      <header className="cc-debt-card__header"><h6>E – Solar <span>Panel</span></h6></header>
      <fieldset className="cc-solar-question cc-solar-question--followup">
        <legend className="label-title">Do you have solar panels on your home?</legend>
        <div className="cc-tax-debt-card__options">
          {["yes", "no"].map((answer) => (
            <label className="form-check" key={answer}>
              <Field className="form-check-input" type="radio" name="hasSolarPanels" value={answer} onChange={() => { setValues(current => ({ ...current, hasSolarPanels: answer, hasOutstandingSolarLoan: "", solarLoanAmount: "" }), true); }} />
              <span>{answer === "yes" ? "Yes" : "No"}</span>
            </label>
          ))}
        </div>
        <ErrorMessage name="hasSolarPanels" component="div" className="error text-danger" />
      </fieldset>
      {hasSolarPanels === "yes" && (
        <fieldset className="cc-solar-question cc-solar-question--followup">
          <legend className="label-title">Do you currently have a loan that is outstanding on the solar panels?</legend>
          <div className="cc-tax-debt-card__options">
            {["yes", "no"].map((answer) => (
              <label className="form-check" key={answer}>
                <Field className="form-check-input" type="radio" name="hasOutstandingSolarLoan" value={answer} onChange={() => { setValues(current => ({ ...current, hasOutstandingSolarLoan: answer, solarLoanAmount: "" }), true); }} />
                <span>{answer === "yes" ? "Yes" : "No"}</span>
              </label>
            ))}
          </div>
          <ErrorMessage name="hasOutstandingSolarLoan" component="div" className="error text-danger" />
          {values.hasOutstandingSolarLoan === "yes" && (
            <div className="input_wrap cc-money-field cc-solar-panel-card__amount">
              <label className="label-title" htmlFor="solarLoanAmount">Outstanding solar panel loan amount</label>
              <Field id="solarLoanAmount" name="solarLoanAmount" type="number" min="0.01" max="9999999999.99" step="0.01" inputMode="decimal" className="form-control" placeholder="$ 0.00" />
              <ErrorMessage name="solarLoanAmount" component="div" className="error text-danger" />
            </div>
          )}
        </fieldset>
      )}
    </section>
  );
};

const AssetsForm = ({ values, setFieldValue, activeStep = 1 }) => {
  const [editingVehicleIndex, setEditingVehicleIndex] = useState(null);
  const [editingPropertyIndex, setEditingPropertyIndex] = useState(null);
  const [editingOtherAssetIndex, setEditingOtherAssetIndex] = useState(null);

  const vehicles = (values.vehicles || []).filter(hasVehicleEntryData);
  const vehicleDraft = values.vehicleDraft || createVehicleRow();
  const updateVehicleDraft = (field, value) => {
    setFieldValue("vehicleDraft", { ...vehicleDraft, [field]: value });
  };
  const saveVehicleDraft = () => {
    if (!hasVehicleEntryData(vehicleDraft)) return;
    const nextVehicles = [...vehicles];
    if (editingVehicleIndex === null) nextVehicles.push({ ...vehicleDraft });
    else nextVehicles[editingVehicleIndex] = { ...vehicleDraft };
    setFieldValue("vehicles", nextVehicles);
    setFieldValue("vehicleDraft", createVehicleRow());
    setEditingVehicleIndex(null);
  };
  const editVehicle = (index) => {
    setFieldValue("vehicleDraft", { ...vehicles[index] });
    setEditingVehicleIndex(index);
  };
  const removeVehicle = (index) => {
    setFieldValue("vehicles", vehicles.filter((_, rowIndex) => rowIndex !== index));
    if (editingVehicleIndex === index) {
      setFieldValue("vehicleDraft", createVehicleRow());
      setEditingVehicleIndex(null);
    } else if (editingVehicleIndex !== null && editingVehicleIndex > index) {
      setEditingVehicleIndex(editingVehicleIndex - 1);
    }
  };
  const cancelVehicleEdit = () => {
    setFieldValue("vehicleDraft", createVehicleRow());
    setEditingVehicleIndex(null);
  };

  const properties = (values.realEstate || []).filter(hasPropertyEntryData);
  const propertyDraft = values.propertyDraft || createRealEstateRow();
  const updatePropertyDraft = (field, value) => {
    setFieldValue("propertyDraft", { ...propertyDraft, [field]: value });
  };
  const savePropertyDraft = () => {
    if (!hasPropertyEntryData(propertyDraft)) return;
    const nextProperties = [...properties];
    if (editingPropertyIndex === null) nextProperties.push({ ...propertyDraft });
    else nextProperties[editingPropertyIndex] = { ...propertyDraft };
    setFieldValue("realEstate", nextProperties);
    setFieldValue("propertyDraft", createRealEstateRow());
    setEditingPropertyIndex(null);
  };
  const editProperty = (index) => {
    setFieldValue("propertyDraft", { ...properties[index] });
    setEditingPropertyIndex(index);
  };
  const removeProperty = (index) => {
    setFieldValue("realEstate", properties.filter((_, rowIndex) => rowIndex !== index));
    if (editingPropertyIndex === index) {
      setFieldValue("propertyDraft", createRealEstateRow());
      setEditingPropertyIndex(null);
    } else if (editingPropertyIndex !== null && editingPropertyIndex > index) {
      setEditingPropertyIndex(editingPropertyIndex - 1);
    }
  };
  const cancelPropertyEdit = () => {
    setFieldValue("propertyDraft", createRealEstateRow());
    setEditingPropertyIndex(null);
  };

  const otherAssets = (values.otherAssets || []).filter(hasOtherAssetEntryData);
  const otherAssetDraft = values.otherAssetDraft || createOtherAssetRow();
  const updateOtherAssetDraft = (field, value) => {
    setFieldValue("otherAssetDraft", { ...otherAssetDraft, [field]: value });
  };
  const saveOtherAssetDraft = () => {
    if (!hasOtherAssetEntryData(otherAssetDraft)) return;
    const nextAssets = [...otherAssets];
    if (editingOtherAssetIndex === null) nextAssets.push({ ...otherAssetDraft });
    else nextAssets[editingOtherAssetIndex] = { ...otherAssetDraft };
    setFieldValue("otherAssets", nextAssets);
    setFieldValue("otherAssetDraft", createOtherAssetRow());
    setEditingOtherAssetIndex(null);
  };
  const editOtherAsset = (index) => {
    setFieldValue("otherAssetDraft", { ...otherAssets[index] });
    setEditingOtherAssetIndex(index);
  };
  const removeOtherAsset = (index) => {
    setFieldValue("otherAssets", otherAssets.filter((_, rowIndex) => rowIndex !== index));
    if (editingOtherAssetIndex === index) {
      setFieldValue("otherAssetDraft", createOtherAssetRow());
      setEditingOtherAssetIndex(null);
    } else if (editingOtherAssetIndex !== null && editingOtherAssetIndex > index) {
      setEditingOtherAssetIndex(editingOtherAssetIndex - 1);
    }
  };
  const cancelOtherAssetEdit = () => {
    setFieldValue("otherAssetDraft", createOtherAssetRow());
    setEditingOtherAssetIndex(null);
  };

  const renderMoneyField = (name) => (
    <div className="input_wrap cc-money-field" key={name}>
      <label className="label-title">{name}</label>
      <Field type="number" step="0.01" inputMode="decimal" min="0" name={`["${name}"]`} className="form-control" placeholder="$ 0.00" />
      <ErrorMessage name={`["${name}"]`} component="div" className="error text-danger" />
    </div>
  );

  return (
    <div className="mt-4 cc-assets-form">
      <h5 className="mb-3" style={{ color: "#4E9C4B" }}>Assets</h5>

      {activeStep === 1 && (
        <>
          <p className="cc-assets-form__intro">
            Now we will list the main things you own that have value. This includes cash, bank accounts, vehicles, and any real estate. These questions are part of the required budget analysis and help your counselor understand your full financial picture.
          </p>
          <section className="cc-asset-section cc-asset-section--narrow">
            <h6 className="cc-budget-form-section__title">Cash and Bank Accounts</h6>
            <div className="cc-asset-fields-stack">
              {[
                "Cash on hand",
                "Checking account balances (total of all checking accounts)",
                "Savings account balances (total of all savings accounts)",
              ].map(renderMoneyField)}
            </div>
          </section>
        </>
      )}

      {activeStep === 2 && (
        <>
          <section className="cc-asset-section cc-asset-section--narrow">
            <h6 className="cc-budget-form-section__title">Retirement and Investments</h6>
            <div className="cc-asset-fields-stack">
              {[
                "Retirement accounts (401(k), 403(b), IRA, pension, etc.)",
                "Other investments (stocks, bonds, mutual funds, crypto, etc.)",
              ].map(renderMoneyField)}
            </div>
          </section>
          <div className="cc-budget-form-note cc-asset-note">
            <i className="fa-regular fa-circle-question" aria-hidden="true" />
            <span>If you are not sure of the exact balance, use your best estimate. Retirement accounts may be treated differently from other assets. Only an attorney can explain how the law would apply to you.</span>
          </div>
        </>
      )}

      {activeStep === 3 && (
        <section className="cc-asset-section">
          <h6 className="cc-budget-form-section__title">Vehicles</h6>
          {vehicles.length > 0 && (
            <div className="cc-debt-summary-wrap cc-vehicle-summary-wrap">
              <table className="cc-debt-summary-table cc-vehicle-summary-table">
                <thead>
                  <tr>
                    <th>Vehicle type</th>
                    <th>Year and model</th>
                    <th>Approx. value</th>
                    <th>Amount owed</th>
                    <th><span className="visually-hidden">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {vehicles.map((vehicle, index) => (
                    <tr key={`vehicle-saved-${index}`}>
                      <td>{vehicle.type || "-"}</td>
                      <td>{vehicle.model || "-"}</td>
                      <td>{vehicle.value ? `$${vehicle.value}` : "-"}</td>
                      <td>{vehicle.owed ? `$${vehicle.owed}` : "-"}</td>
                      <td>
                        <div className="cc-debt-summary-table__actions">
                          <button type="button" className="cc-debt-row-action is-edit" onClick={() => editVehicle(index)} aria-label={`Edit vehicle ${index + 1}`}>
                            <i className="fa-solid fa-pen" aria-hidden="true" />
                          </button>
                          <button type="button" className="cc-debt-row-action is-delete" onClick={() => removeVehicle(index)} aria-label={`Delete vehicle ${index + 1}`}>
                            <i className="fa-solid fa-trash" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="cc-vehicle-editor">
            <div className="cc-vehicle-editor__heading">
              <strong>{editingVehicleIndex === null ? "Add vehicle" : `Editing vehicle #${editingVehicleIndex + 1}`}</strong>
              {editingVehicleIndex !== null && (
                <button type="button" onClick={cancelVehicleEdit}>Cancel edit</button>
              )}
            </div>
            <div className="cc-vehicle-grid">
              <div className="input_wrap">
                <label className="label-title">Vehicle type</label>
                <DebtTypeSelect value={vehicleDraft.type} options={VEHICLE_TYPES} onChange={(value) => updateVehicleDraft("type", value)} label="Select vehicle type" />
              </div>
              <div className="input_wrap">
                <label className="label-title">Year and model</label>
                <input className="form-control" value={vehicleDraft.model} onChange={(event) => updateVehicleDraft("model", event.target.value)} placeholder="e.g. 2016 Honda Civic" />
              </div>
              <div className="input_wrap">
                <label className="label-title">Approximate value</label>
                <input type="number" step="0.01" inputMode="decimal" min="0" className="form-control" value={vehicleDraft.value} onChange={(event) => updateVehicleDraft("value", event.target.value)} placeholder="$ 0.00" />
              </div>
              <div className="input_wrap">
                <label className="label-title">Amount owed on loan (if any)</label>
                <input type="number" step="0.01" inputMode="decimal" min="0" className="form-control" value={vehicleDraft.owed} onChange={(event) => updateVehicleDraft("owed", event.target.value)} placeholder="$ 0.00" />
              </div>
            </div>
          </div>
          <button type="button" className="cc-debt-add cc-asset-add" onClick={saveVehicleDraft} disabled={!hasVehicleEntryData(vehicleDraft)}>
            <i className={`fa-solid ${editingVehicleIndex === null ? "fa-plus" : "fa-check"}`} aria-hidden="true" />
            <span>
              {editingVehicleIndex !== null
                ? "Update vehicle"
                : "Add more vehicles"}
            </span>
          </button>
        </section>
      )}

      {activeStep === 4 && (
        <section className="cc-asset-section">
          <h6 className="cc-budget-form-section__title">Real Estate</h6>
          <div className="cc-asset-radio-question">
            <label className="label-title">Do you own a home or other real estate?</label>
            <div className="cc-tax-debt-card__options">
              <label className="form-check"><Field className="form-check-input" type="radio" name="ownsRE" value="yes" /><span>Yes</span></label>
              <label className="form-check"><Field className="form-check-input" type="radio" name="ownsRE" value="no" /><span>No</span></label>
            </div>
          </div>
          {values.ownsRE === "yes" && (
            <>
              {properties.length > 0 && (
                <div className="cc-debt-summary-wrap cc-property-summary-wrap">
                  <table className="cc-debt-summary-table cc-property-summary-table">
                    <thead>
                      <tr>
                        <th>Property type</th>
                        <th>Approx. market value</th>
                        <th>Amount owed</th>
                        <th><span className="visually-hidden">Actions</span></th>
                      </tr>
                    </thead>
                    <tbody>
                      {properties.map((property, index) => (
                        <tr key={`property-saved-${index}`}>
                          <td>{property.type || "-"}</td>
                          <td>{property.value ? `$${property.value}` : "-"}</td>
                          <td>{property.owed ? `$${property.owed}` : "-"}</td>
                          <td>
                            <div className="cc-debt-summary-table__actions">
                              <button type="button" className="cc-debt-row-action is-edit" onClick={() => editProperty(index)} aria-label={`Edit property ${index + 1}`}>
                                <i className="fa-solid fa-pen" aria-hidden="true" />
                              </button>
                              <button type="button" className="cc-debt-row-action is-delete" onClick={() => removeProperty(index)} aria-label={`Delete property ${index + 1}`}>
                                <i className="fa-solid fa-trash" aria-hidden="true" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="cc-vehicle-editor cc-property-editor">
                <div className="cc-vehicle-editor__heading">
                  <strong>{editingPropertyIndex === null ? "Add property" : `Editing property #${editingPropertyIndex + 1}`}</strong>
                  {editingPropertyIndex !== null && (
                    <button type="button" onClick={cancelPropertyEdit}>Cancel edit</button>
                  )}
                </div>
                <div className="cc-property-grid">
                  <div className="input_wrap">
                    <label className="label-title">Property type</label>
                    <DebtTypeSelect value={propertyDraft.type} options={PROPERTY_TYPES} onChange={(value) => updatePropertyDraft("type", value)} label="Select property type" />
                  </div>
                  <div className="input_wrap">
                    <label className="label-title">Approximate market value</label>
                    <input type="number" step="0.01" inputMode="decimal" min="0" className="form-control" value={propertyDraft.value} onChange={(event) => updatePropertyDraft("value", event.target.value)} placeholder="$ 0.00" />
                  </div>
                  <div className="input_wrap">
                    <label className="label-title">Amount owed on mortgages or home equity loans</label>
                    <input type="number" step="0.01" inputMode="decimal" min="0" className="form-control" value={propertyDraft.owed} onChange={(event) => updatePropertyDraft("owed", event.target.value)} placeholder="$ 0.00" />
                  </div>
                </div>
              </div>
              <button type="button" className="cc-debt-add cc-asset-add" onClick={savePropertyDraft} disabled={!hasPropertyEntryData(propertyDraft)}>
                <i className={`fa-solid ${editingPropertyIndex === null ? "fa-plus" : "fa-check"}`} aria-hidden="true" />
                <span>
                  {editingPropertyIndex !== null
                    ? "Update property"
                    : "Add More Properties"}
                </span>
              </button>
            </>
          )}
        </section>
      )}

      {activeStep === 5 && (
        <section className="cc-asset-section">
          <h6 className="cc-budget-form-section__title">Other Significant Assets</h6>
          {otherAssets.length > 0 && (
            <div className="cc-debt-summary-wrap cc-other-asset-summary-wrap">
              <table className="cc-debt-summary-table cc-other-asset-summary-table">
                <thead>
                  <tr>
                    <th>Business or equipment</th>
                    <th>Value</th>
                    <th>Other significant item</th>
                    <th>Value</th>
                    <th><span className="visually-hidden">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {otherAssets.map((asset, index) => (
                    <tr key={`other-asset-saved-${index}`}>
                      <td>{asset.businessDescription || "-"}</td>
                      <td>{asset.businessValue ? `$${asset.businessValue}` : "-"}</td>
                      <td>{asset.otherDescription || "-"}</td>
                      <td>{asset.otherValue ? `$${asset.otherValue}` : "-"}</td>
                      <td>
                        <div className="cc-debt-summary-table__actions">
                          <button type="button" className="cc-debt-row-action is-edit" onClick={() => editOtherAsset(index)} aria-label={`Edit significant asset ${index + 1}`}>
                            <i className="fa-solid fa-pen" aria-hidden="true" />
                          </button>
                          <button type="button" className="cc-debt-row-action is-delete" onClick={() => removeOtherAsset(index)} aria-label={`Delete significant asset ${index + 1}`}>
                            <i className="fa-solid fa-trash" aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="cc-vehicle-editor cc-other-asset-editor">
            <div className="cc-vehicle-editor__heading">
              <strong>{editingOtherAssetIndex === null ? "Add significant asset" : `Editing significant asset #${editingOtherAssetIndex + 1}`}</strong>
              {editingOtherAssetIndex !== null && (
                <button type="button" onClick={cancelOtherAssetEdit}>Cancel edit</button>
              )}
            </div>
            <div className="cc-other-assets-grid">
              <label className="label-title">Business ownership or valuable equipment</label>
              <input className="form-control" value={otherAssetDraft.businessDescription} onChange={(event) => updateOtherAssetDraft("businessDescription", event.target.value)} placeholder="Description" />
              <input type="number" step="0.01" inputMode="decimal" min="0" className="form-control" value={otherAssetDraft.businessValue} onChange={(event) => updateOtherAssetDraft("businessValue", event.target.value)} placeholder="$ 0.00" />
              <label className="label-title">Other items of significant value (for example: collection, jewelry, etc.)</label>
              <input className="form-control" value={otherAssetDraft.otherDescription} onChange={(event) => updateOtherAssetDraft("otherDescription", event.target.value)} placeholder="Description" />
              <input type="number" step="0.01" inputMode="decimal" min="0" className="form-control" value={otherAssetDraft.otherValue} onChange={(event) => updateOtherAssetDraft("otherValue", event.target.value)} placeholder="$ 0.00" />
            </div>
          </div>
          <button type="button" className="cc-debt-add cc-asset-add" onClick={saveOtherAssetDraft} disabled={!hasOtherAssetEntryData(otherAssetDraft)}>
            <i className={`fa-solid ${editingOtherAssetIndex === null ? "fa-plus" : "fa-check"}`} aria-hidden="true" />
            <span>
              {editingOtherAssetIndex !== null
                ? "Update significant asset"
                : "Add More Assets"}
            </span>
          </button>
        </section>
      )}
    </div>
  );
};

const HOW_GROUPS = [
  {
    title: "Life Events That Affected My Finances",
    key: "lifeEvents",
    items: ["Illness or injury", "Separation or divorce", "Death of a spouse or close family member", "Job loss or reduced hours", "Business loss or failure", "Unexpected caregiving responsibilities", "Sudden drop in household income"],
  },
  {
    title: "Behavioral and Emotional Patterns",
    key: "behavioral",
    items: ["Gambling or other addictions", "Overspending during emotional stress", "Shopping to cope with emotions", "Avoiding bills or delaying opening mail", "Not tracking spending", "Poor communication about money with a partner"],
  },
  {
    title: "Practical and Financial Decisions",
    key: "practical",
    items: ["Major unnecessary purchases", "High credit card spending", "Unaffordable car purchase", "Unaffordable house payment", "Taking on loans without understanding terms", "Relying on variable income without a cushion"],
  },
];

const HowGotHereForm = ({ values, setFieldValue, errors, touched }) => {
  const toggleGroup = (groupKey, item) => {
    const curr = values[groupKey] || [];
    setFieldValue(groupKey, curr.includes(item) ? curr.filter((i) => i !== item) : [...curr, item]);
  };
  return (
    <div className="mt-4">
      <h5 className="mb-3" style={{ color: "#4E9C4B" }}>How I Got Here</h5>
      {HOW_GROUPS.map((g) => (
        <div key={g.key} className="mb-4">
          <p className="fw-bold mb-2" style={{ color: "#4E9C4B", borderBottom: "1px solid #e0e0e0", paddingBottom: "6px" }}>{g.title}</p>
          {g.items.map((item) => (
            <div className="form-check mb-2" key={item}>
              <input className="form-check-input" type="checkbox" checked={(values[g.key] || []).includes(item)} onChange={() => toggleGroup(g.key, item)} id={g.key + "_" + item} />
              <label className="form-check-label" htmlFor={g.key + "_" + item}>{item}</label>
            </div>
          ))}
        </div>
      ))}
      <p className="fw-bold mb-2" style={{ color: "#4E9C4B" }}>Identifying Patterns</p>
      {[
        { key: "behaviors", label: "What behaviors or habits show up more than once in my financial history?" },
        { key: "emotions", label: "What thoughts or emotions tend to influence my financial decisions?" },
        { key: "response", label: "How do I usually respond when money becomes tight?" },
        { key: "nextStep", label: "One step I can take in the next week" },
      ].map((q) => (
        <div className="mb-3" key={q.key}>
          <div className="input_wrap">
            <label className="label-title">{q.label}<Req formName="how_got_here" fieldKey={q.key} /></label>
            <Field as="textarea" name={q.key} className={`form-control${errors[q.key] && touched[q.key] ? " is-invalid" : ""}`} rows={3} placeholder="Write your thoughts here..." />
            <ErrorMessage name={q.key} component="div" className="error text-danger" />
          </div>
        </div>
      ))}
    </div>
  );
};

const COMPARISON_QUESTIONS = [
  { key: "q1", text: "Can I realistically repay everything?", options: ["I can realistically repay everything (or almost everything) in the next two years.", "I cannot realistically repay everything in the next two years."] },
  { key: "q2", text: "Do I need legal protection right now?", options: ["I do NOT need legal protection right now.", "I DO need legal protection right now (lawsuits, garnishments, foreclosure, repossession)."] },
  { key: "q3", text: "How stable is my income?", options: ["My income is stable and predictable.", "My income is unstable or has dropped long-term."] },
  { key: "q4", text: "How long can I carry this stress?", options: ["I can carry this stress for several years if needed.", "I cannot carry this stress much longer."] },
];

const ComparisonForm = ({ values, setFieldValue, errors, touched }) => (
  <div className="mt-4">
    <h5 className="mb-1" style={{ color: "#4E9C4B" }}>Comparing Your Options</h5>
    <small className="text-muted d-block mb-3">For each question, choose the answer that best matches your current reality.</small>
    {COMPARISON_QUESTIONS.map((q) => (
      <div className="mb-4 p-3" key={q.key} style={{ background: "#f9f9f9", borderRadius: "8px", border: "1px solid #e0e0e0" }}>
        <label className="label-title mb-2">{q.text}<Req formName="comparison" fieldKey={q.key} /></label>
        {q.options.map((opt) => (
          <div className="form-check mb-1" key={opt}>
            <Field className="form-check-input" type="radio" name={q.key} value={opt} id={q.key + "_" + opt} />
            <label className="form-check-label" htmlFor={q.key + "_" + opt}>{opt}</label>
          </div>
        ))}
        <ErrorMessage name={q.key} component="div" className="error text-danger" />
      </div>
    ))}
    <div className="mb-3">
      <label className="label-title">Would you like help finding a bankruptcy attorney?<Req formName="comparison" fieldKey="attorney" /></label>
      {["Yes, please connect me with a neutral attorney referral service.", "No, I already have an attorney.", "No, I am filing pro se."].map((opt) => (
        <div className="form-check mb-1" key={opt}>
          <Field className="form-check-input" type="radio" name="attorney" value={opt} id={"atty_" + opt} />
          <label className="form-check-label" htmlFor={"atty_" + opt}>{opt}</label>
        </div>
      ))}
      <ErrorMessage name="attorney" component="div" className="error text-danger" />
    </div>
  </div>
);

const BudgetReviewConfirmForm = ({ reviewSummary = {} }) => {
  const formatCurrency = (amount) => new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(Number(amount) || 0);

  const rows = [
    ["Total net monthly income", reviewSummary.netIncome],
    ["Total monthly living expenses", reviewSummary.livingExpenses],
    ["Total minimum payments on secured debts", reviewSummary.securedPayments],
    ["Total minimum payments on unsecured debts", reviewSummary.unsecuredPayments],
    ["Estimated monthly surplus or shortfall", reviewSummary.surplus],
    ["Total unsecured debt balance", reviewSummary.unsecuredBalance],
    ["Total secured debt balance", reviewSummary.securedBalance],
  ];

  return (
    <div className="mt-4 cc-review-confirm">
      <p className="cc-review-confirm__intro">
        You are almost finished with the budget analysis. Below is a summary of what you entered. Please take a moment to review it. If something looks off, you can go back and make changes.
      </p>
      <div className="cc-review-summary" aria-label="Budget summary">
        {rows.map(([label, amount]) => (
          <div className="cc-review-summary__row" key={label}>
            <span>{label}</span>
            <strong className={label.includes("surplus or shortfall") && Number(amount) < 0 ? "is-negative" : ""}>
              {formatCurrency(amount)}
            </strong>
          </div>
        ))}
      </div>
    </div>
  );
};

const FORM_MAP = {
  budget_household: { Component: BudgetHouseholdForm, buildSchema: buildBudgetHouseholdSchema, initialValues: INITIAL_VALUES.budget_household },
  monthly_income: { Component: MonthlyIncomeForm, buildSchema: buildMonthlyIncomeSchema, initialValues: INITIAL_VALUES.monthly_income },
  monthly_expenses: { Component: MonthlyExpensesForm, buildSchema: buildMonthlyExpensesSchema, initialValues: INITIAL_VALUES.monthly_expenses },
  debts: { Component: DebtsForm, buildSchema: buildDebtsSchema, initialValues: INITIAL_VALUES.debts },
  legal_actions: { Component: LegalActionsForm, buildSchema: buildLegalActionsSchema, initialValues: INITIAL_VALUES.legal_actions },
  assets: { Component: AssetsForm, buildSchema: buildAssetsSchema, initialValues: INITIAL_VALUES.assets },
  review_confirm: { Component: BudgetReviewConfirmForm, buildSchema: buildReviewConfirmSchema, initialValues: INITIAL_VALUES.review_confirm },
  how_got_here: { Component: HowGotHereForm, buildSchema: buildHowGotHereSchema, initialValues: INITIAL_VALUES.how_got_here },
  comparison: { Component: ComparisonForm, buildSchema: buildComparisonSchema, initialValues: INITIAL_VALUES.comparison },
};

// Reused by the dashboard Budget Analysis editor so both entry points keep the
// same fields, validation rules, and API payload shape.
export const CLASS_FIVE_BUDGET_FORM_MAP = Object.freeze({
  budget_household: FORM_MAP.budget_household,
  monthly_income: FORM_MAP.monthly_income,
  monthly_expenses: FORM_MAP.monthly_expenses,
  debts: FORM_MAP.debts,
  legal_actions: FORM_MAP.legal_actions,
  assets: FORM_MAP.assets,
});

export { prepareClassFiveFormValues };
// ─────────────────────────────────────────────────────────────────────────────
// QUIZ COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────────────
// QUIZ DATA  (unchanged — paste your existing QUIZ_DATA here)
// ─────────────────────────────────────────────────────────────────────────────
// const QUIZ_DATA = { ... };   ← keep as-is

// ─────────────────────────────────────────────────────────────────────────────
// CLASS-6 FORM  — "How I Got Here And What Can Help Me Move Forward"
// ─────────────────────────────────────────────────────────────────────────────

const HOW_LIFE_EVENTS = [
  { key: "c1",  label: "Illness or injury",                          support: "Medical billing advocate; disability benefits review; social worker; payment plan review with providers" },
  { key: "c2",  label: "Separation or divorce",                      support: "Family law attorney; mediator; therapist; budgeting support" },
  { key: "c3",  label: "Death of a spouse or close family member",   support: "Grief counseling; estate attorney; support group; benefits review" },
  { key: "c4",  label: "Job loss or reduced hours",                  support: "Unemployment benefits; job training programs; resume and job search support; staffing agencies" },
  { key: "c5",  label: "Business loss or business failure",          support: "Small business counselor; CPA; debt negotiation options; legal advice" },
  { key: "c6",  label: "Unexpected caregiving responsibilities",     support: "Community resources; respite care; benefits review" },
  { key: "c7",  label: "Sudden drop in household income",            support: "State or local assistance programs; budgeting help; temporary income support" },
];

const HOW_EMOTIONAL_PATTERNS = [
  { key: "c1", label: "Gambling or other addictions",              support: "Therapist or counselor; addiction support group; accountability partner" },
  { key: "c2", label: "Overspending during emotional stress",      support: "Therapist; financial coach; spending journal; automatic bill payments" },
  { key: "c3", label: "Shopping to cope with emotions",            support: "Mental health support; peer or faith based support group; alternative coping routines" },
  { key: "c4", label: "Avoiding bills or delaying opening mail",   support: "Weekly paperwork routine; support from a trusted person; therapist" },
  { key: "c5", label: "Not tracking spending",                     support: "Budgeting app; paper tracker; monthly check in" },
  { key: "c6", label: "Poor communication about money with a partner", support: "Couples counselor; financial planner; regular money meetings" },
];

const HOW_FINANCIAL_DECISIONS = [
  { key: "c1", label: "Major unnecessary purchases",              support: "Return policy review; selling unused items; setting spending limits" },
  { key: "c2", label: "High credit card spending",                support: "Budget plan; automated minimum payments; credit counselor review" },
  { key: "c3", label: "Unaffordable car purchase",                support: "Refinance review; sell or trade options; transportation alternatives" },
  { key: "c4", label: "Unaffordable house payment",               support: "Housing counselor; refinance review; legal advice if needed" },
  { key: "c5", label: "Taking on loans without understanding terms", support: "Financial education; loan officer review; written questions before signing" },
  { key: "c6", label: "Relying on variable income without a cushion", support: "Emergency fund plan; automatic savings transfers; smoothing income where possible" },
];

const HOW_SYSTEM_ACCESS = [
  { key: "c1", label: "Medical bills from uncovered care",  support: "Appeals; charity care applications; hospital financial counselor" },
  { key: "c2", label: "Lack of stable childcare",           support: "Subsidized childcare programs; community or faith based resources" },
  { key: "c3", label: "Transportation issues or rising costs", support: "Insurance review; carpooling; public transit options where available" },
  { key: "c4", label: "Loss of government benefits",        support: "Benefits counselor; legal advocate or legal aid" },
];

/**
 * Always-visible, two-column worksheet section used inside the Class-6 form.
 */
const HowWorksheetSection = ({
  sectionNumber,
  title,
  columns,
  items,
  checkedState,
  onToggle,
}) => {
  const headingId = `how_${title.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`;
  return (
    <section className="cc-how-worksheet-section" aria-labelledby={headingId}>
      <div className="cc-how-worksheet-section__heading">
        <h6 id={headingId}><span>{sectionNumber}.</span> {title}</h6>
      </div>
      <div className="cc-how-worksheet-section__columns">
        {columns.map((column, index) => (
          <span key={`${headingId}_column_${index}`} aria-hidden={!column}>{column}</span>
        ))}
      </div>
      <div className="cc-how-worksheet-section__body">
        {items.map((item) => (
          <div className="cc-how-worksheet-row" key={item.key}>
            <div className="cc-how-table-check">
              <input
                type="checkbox"
                id={`${headingId}_${item.key}`}
                checked={checkedState[item.key] || false}
                onChange={() => onToggle(item.key)}
                className="cc-how-table-check__input"
              />
              <label
                htmlFor={`${headingId}_${item.key}`}
                className="cc-how-table-check__label"
              >
                <span className="cc-how-table-check__mark" aria-hidden="true">
                  <i className="fa-solid fa-check" />
                </span>
                <span className="visually-hidden">Select {item.label}</span>
              </label>
            </div>
            <label className="cc-how-worksheet-row__item" htmlFor={`${headingId}_${item.key}`}>
              {item.label}
            </label>
            <p className="cc-how-worksheet-row__support" data-label={columns[2]}>{item.support}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

/**
 * HowGotHereFullForm
 * Shown as Class-6 standalone form (after video, no quiz for this class).
 * Saves to user/user_assessment with howDidThis, emotionPattern, financialDecision,
 * systemAccess, identifyPattern, nextSteps keys.
 */
const HowGotHereFullForm = ({ onSubmit, onBack, onSaveLater, loading, dataLoading, initialData }) => {
  const toSelectedState = (values = {}) => Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      value === true || String(value).toLowerCase() === "checked",
    ])
  );
  const [lifeEvents, setLifeEvents] = React.useState(() => toSelectedState(initialData?.howDidThis));
  const [emotional, setEmotional] = React.useState(() => toSelectedState(initialData?.emotionPattern));
  const [financial, setFinancial] = React.useState(() => toSelectedState(initialData?.financialDecision));
  const [systemAccess, setSystemAccess] = React.useState(() => toSelectedState(initialData?.systemAccess));

  // Identifying Patterns text fields (a1–a5)
  const [a1, setA1] = React.useState(initialData?.identifyPattern?.a1 || ""); // behaviors / habits
  const [a2, setA2] = React.useState(initialData?.identifyPattern?.a2 || ""); // thoughts / emotions
  const [a3, setA3] = React.useState(initialData?.identifyPattern?.a3 || ""); // how respond when tight
  const [a4, setA4] = React.useState(initialData?.identifyPattern?.a4 || ""); // patterns feel recent
  const [a5, setA5] = React.useState(initialData?.identifyPattern?.a5 || ""); // patterns already improved

  // My Next Steps (a1–a3 text fields)
  const [ns1, setNs1] = React.useState(initialData?.nextSteps?.a1 || "");
  const [ns2, setNs2] = React.useState(initialData?.nextSteps?.a2 || "");
  const [ns3, setNs3] = React.useState(initialData?.nextSteps?.a3 || "");
  const [pdfDownloading, setPdfDownloading] = React.useState(false);

  React.useEffect(() => {
    if (!initialData) return;
    setLifeEvents(toSelectedState(initialData.howDidThis));
    setEmotional(toSelectedState(initialData.emotionPattern));
    setFinancial(toSelectedState(initialData.financialDecision));
    setSystemAccess(toSelectedState(initialData.systemAccess));
    setA1(initialData.identifyPattern?.a1 || "");
    setA2(initialData.identifyPattern?.a2 || "");
    setA3(initialData.identifyPattern?.a3 || "");
    setA4(initialData.identifyPattern?.a4 || "");
    setA5(initialData.identifyPattern?.a5 || "");
    setNs1(initialData.nextSteps?.a1 || "");
    setNs2(initialData.nextSteps?.a2 || "");
    setNs3(initialData.nextSteps?.a3 || "");
  }, [initialData]);

  const toggleSection = (setter) => (key) =>
    setter((prev) => ({ ...prev, [key]: !prev[key] }));

  const toCheckedMap = (state, keys) =>
    Object.fromEntries(keys.map((k) => [k, state[k] ? "Checked" : ""]));

  const handleSubmit = () => {
    const payload = {
      howDidThis:       { ...toCheckedMap(lifeEvents, HOW_LIFE_EVENTS.map((i) => i.key)) },
      emotionPattern:   { ...toCheckedMap(emotional,  HOW_EMOTIONAL_PATTERNS.map((i) => i.key)) },
      financialDecision:{ ...toCheckedMap(financial,  HOW_FINANCIAL_DECISIONS.map((i) => i.key)) },
      systemAccess:     { ...toCheckedMap(systemAccess, HOW_SYSTEM_ACCESS.map((i) => i.key)) },
      identifyPattern:  { a1, a2, a3, a4, a5 },
      nextSteps:        { a1: ns1, a2: ns2, a3: ns3 },
    };
    onSubmit(payload);
  };

  const handlePdfDownload = async () => {
    setPdfDownloading(true);
    try {
      await new Promise((resolve) => window.requestAnimationFrame(resolve));
      const { downloadHowGotHerePdf } = await import("../../utils/howGotHerePdf");
      await downloadHowGotHerePdf({
        sections: [
          {
            number: 1,
            title: "Life Events That Affected My Finances",
            headers: ["", "Life Events That Affected My Finances", "Supports or Resources"],
            items: HOW_LIFE_EVENTS.map((item) => ({ ...item, selected: Boolean(lifeEvents[item.key]) })),
          },
          {
            number: 2,
            title: "Behavioral and Emotional Patterns",
            headers: ["Check Those That Apply", "Issue or Event", "Circle Supports or Resources"],
            items: HOW_EMOTIONAL_PATTERNS.map((item) => ({ ...item, selected: Boolean(emotional[item.key]) })),
          },
          {
            number: 3,
            title: "Practical and Financial Decisions",
            headers: ["Check Those That Apply", "Issue or Event", "Circle Supports or Resources"],
            items: HOW_FINANCIAL_DECISIONS.map((item) => ({ ...item, selected: Boolean(financial[item.key]) })),
          },
          {
            number: 4,
            title: "System and Access Factors",
            headers: ["Check Those That Apply", "Issue or Event", "Circle Supports or Resources"],
            items: HOW_SYSTEM_ACCESS.map((item) => ({ ...item, selected: Boolean(systemAccess[item.key]) })),
          },
        ],
        identifyingPatterns: [a1, a2, a3, a4, a5],
        nextSteps: [ns1, ns2, ns3],
      });
    } catch (error) {
      console.error("Unable to generate How I Got Here PDF", error);
      toast.error("The PDF could not be generated. Please try again.");
    } finally {
      setPdfDownloading(false);
    }
  };

  if (dataLoading) {
    return (
      <div className="d-flex justify-content-center py-5" aria-label="Loading saved answers">
        <ThreeDots height="32" width="70" radius="9" color="#4E9C4B" ariaLabel="loading-saved-answers" visible />
      </div>
    );
  }

  return (
    <div className="mt-2">
      {/* Green header banner */}
      <div
        style={{
          background: "#4E9C4B",
          color: "#fff",
          borderRadius: "8px",
          padding: "16px 20px",
          marginBottom: "20px",
        }}
      >
        <h5 style={{ margin: 0, fontWeight: 700, fontSize: "16px" }}>
          How I Got Here and What Can Help Me Move Forward
        </h5>
        <p style={{ margin: "4px 0 0", fontSize: "15px", opacity: 0.9 }}>
          Check any items that apply and use the space to note supports or resources you want to
          explore.
        </p>
      </div>

      <div className="cc-how-worksheet">
        <HowWorksheetSection
          sectionNumber="1"
          title="Life Events That Affected My Finances"
          columns={["", "Life Events That Affected My Finances", "Supports or Resources"]}
          items={HOW_LIFE_EVENTS}
          checkedState={lifeEvents}
          onToggle={toggleSection(setLifeEvents)}
        />
        <HowWorksheetSection
          sectionNumber="2"
          title="Behavioral and Emotional Patterns"
          columns={["Check Those That Apply", "Issue or Event", "Circle Supports or Resources"]}
          items={HOW_EMOTIONAL_PATTERNS}
          checkedState={emotional}
          onToggle={toggleSection(setEmotional)}
        />
        <HowWorksheetSection
          sectionNumber="3"
          title="Practical and Financial Decisions"
          columns={["Check Those That Apply", "Issue or Event", "Circle Supports or Resources"]}
          items={HOW_FINANCIAL_DECISIONS}
          checkedState={financial}
          onToggle={toggleSection(setFinancial)}
        />
        <HowWorksheetSection
          sectionNumber="4"
          title="System and Access Factors"
          columns={["Check Those That Apply", "Issue or Event", "Circle Supports or Resources"]}
          items={HOW_SYSTEM_ACCESS}
          checkedState={systemAccess}
          onToggle={toggleSection(setSystemAccess)}
        />
      </div>

      {/* Identifying Patterns */}
      <section className="cc-how-response-section">
        <div className="cc-how-response-section__heading">
          <h6>Identifying Patterns</h6>
          <p>Take a few minutes to reflect and write your thoughts.</p>
        </div>
        <div className="row g-3">
          {[
            { val: a1, set: setA1, label: "What behaviors or habits show up more than once in my financial history?" },
            { val: a2, set: setA2, label: "What thoughts or emotions tend to influence my financial decisions?" },
            { val: a3, set: setA3, label: "How do I usually respond when money becomes tight?" },
            { val: a4, set: setA4, label: "Which patterns feel recent, and which have been present for years?" },
          ].map((q, idx) => (
            <div className="col-md-6" key={idx}>
              <label className="cc-how-response-label">
                {q.label}
              </label>
              <textarea
                className="form-control"
                rows={3}
                placeholder="Write your thoughts here..."
                value={q.val}
                onChange={(e) => q.set(e.target.value)}
              />
            </div>
          ))}
          <div className="col-12">
            <label className="cc-how-response-label">
              Which patterns have I already improved, even a little?
            </label>
            <textarea
              className="form-control"
              rows={3}
              placeholder="Write your thoughts here..."
              value={a5}
              onChange={(e) => setA5(e.target.value)}
            />
          </div>
        </div>
      </section>

      {/* My Next Steps */}
      <section className="cc-how-response-section cc-how-next-steps">
        <div className="cc-how-response-section__heading">
          <h6>My Next Steps</h6>
          <p>Choose one small action in each area to help you move forward.</p>
        </div>
        <div className="row g-0">
          {[
            { val: ns1, set: setNs1, icon: "fa-arrows-rotate", label: "One habit or pattern I want to change" },
            { val: ns2, set: setNs2, icon: "fa-people-group", label: "One support or resource I want to explore" },
            { val: ns3, set: setNs3, icon: "fa-calendar-check", label: "One step I can take in the next week" },
          ].map((q, idx) => (
            <div className="col-md-4 cc-how-next-step" key={idx}>
              <label className="cc-how-next-step__label">
                <i className={`fa-solid ${q.icon}`} aria-hidden="true" />
                <span>{q.label}</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="Write your answer here..."
                value={q.val}
                onChange={(e) => q.set(e.target.value)}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Footer buttons */}
      <div className="footer-form-btn d-flex flex-wrap mt-4">
        <button type="button" className="btn_brnd cc-action-back mt-4 me-3" onClick={onBack}>
          Go Back
        </button>
        <button type="button" className="btn_brnd cc-action-save-later mt-4 me-3" onClick={onSaveLater}>
          Save and Return Later
        </button>
        <button
          type="button"
          className="btn_brnd cc-action-save-later cc-how-download mt-4 me-3"
          onClick={handlePdfDownload}
          disabled={pdfDownloading}
        >
          {pdfDownloading ? (
            <ThreeDots height="20" width="50" radius="9" color="#347F39" ariaLabel="generating-pdf" visible />
          ) : (
            <>
              <i className="fa-solid fa-download me-2" aria-hidden="true" />
              Download PDF
            </>
          )}
        </button>
        <button
          type="button"
          className="btn_brnd mt-4 btn-next"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <ThreeDots height="20" width="60" radius="9" color="#fff" ariaLabel="three-dots-loading" visible />
          ) : (
            "Save and continue"
          )}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CLASS-7  — Quick Knowledge Check (inline overlay, no quiz page)
// ─────────────────────────────────────────────────────────────────────────────

const CLASS7_QUESTION = {
  text: "The video says that most big problems are not 'you' problems. What kind of problems does it say they usually are?",
  options: [
    "'Time and money' problems",
    "'Who and how' problems",
    "'Cause and effect' problems",
    "'Now or never' problems",
  ],
  correctIndex: 1, // B  — "'Who and how' problems"
  explanation:
    "That's right. The video explains that most big problems are 'who and how' problems, not 'you' problems. The solution is usually a person or a resource outside of yourself, not more willpower. Keep that in mind as you look at your worksheet and ask who can help with each piece, and how.",
};

/**
 * QuickKnowledgeCheckOverlay
 * Renders the green "Quick Knowledge Check" card that overlays the video area
 * for Class 7 (and similarly Class 12).
 *
 * Props:
 *   question      – { text, options, correctIndex }
 *   onCorrect     – called when the user answers correctly and clicks "Save and continue"
 *   onRetry       – called when the user clicks "Try Again" after a wrong answer
 *   onReplaySection – called when the user clicks "Replay the Section"
 */
const QuickKnowledgeCheckOverlay = ({
  question,
  onCorrect,
  onBack,
  onSaveLater,
  onReplaySection,
}) => {
  const [selected, setSelected]       = React.useState(null);
  const [submitted, setSubmitted]     = React.useState(false);
  const [showRetry, setShowRetry]     = React.useState(false);   // wrong-answer modal
  const [showSuccess, setShowSuccess] = React.useState(false);   // right-answer state
  const [attempts, setAttempts]       = React.useState([]);

  const handleSubmit = () => {
    if (selected === null) return;
    setAttempts((previous) => [
      ...previous,
      {
        selectedIndex: selected,
        selectedAnswer: question.options[selected],
        isCorrect: selected === question.correctIndex,
      },
    ]);
    setSubmitted(true);
    if (selected !== question.correctIndex) {
      setShowRetry(true);
    } else {
      setShowSuccess(true);
    }
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    });
  };

  const handleTryAgain = () => {
    setSelected(null);
    setSubmitted(false);
    setShowRetry(false);
    setShowSuccess(false);
  };

  const optionLetter = (i) => ["A", "B", "C", "D"][i];

  const buildAnswersPayload = () => ({
    q1: {
      question: question.text,
      options: question.options,
      selectedIndex: selected,
      selectedAnswer: question.options[selected],
      correctIndex: question.correctIndex,
      correctAnswer: question.options[question.correctIndex],
      isCorrect: selected === question.correctIndex,
      attempts,
    },
  });

  return (
    <div className="cc-qkc">
      {/* ── "Let's give it another look" modal (wrong answer) ── */}
      {showRetry && (
        <div
          className="cc-qkc-retry-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="cc-qkc-retry-title"
          aria-describedby="cc-qkc-retry-description"
        >
          <div className="cc-qkc-retry-modal">
            <div className="cc-qkc-retry-modal__message">
              <span className="cc-qkc-retry-modal__icon" aria-hidden="true">
                <i className="fa-solid fa-xmark" />
              </span>
              <div>
                <h2 id="cc-qkc-retry-title">Let's give it another look</h2>
                <p id="cc-qkc-retry-description">
                  This part of the course is worth understanding well, so
                  we'll replay the section and then come back to the question.
                </p>
              </div>
            </div>
            <div className="cc-qkc-retry-modal__actions">
              <button
                type="button"
                className="cc-qkc-retry-modal__try"
                onClick={handleTryAgain}
              >
                Try Again
              </button>
              <button
                type="button"
                className="cc-qkc-retry-modal__replay"
                onClick={() => {
                  handleTryAgain();
                  onReplaySection();
                }}
              >
                <i className="fa-solid fa-arrow-rotate-left" aria-hidden="true" />
                Replay the Section
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="cc-qkc-card" aria-labelledby="cc-qkc-title">
        <header className="cc-qkc-card__header">
          <span className="cc-qkc-card__header-icon" aria-hidden="true">
            <i className="fa-solid fa-brain" />
          </span>
          <div>
            <h1 id="cc-qkc-title">Quick Knowledge Check</h1>
            <p>
              Time for a quick knowledge check. Let's see if this is sticking
              with you before moving on.
            </p>
          </div>
        </header>

        <div className="cc-qkc-card__body">
          {showSuccess && (
            <div className="cc-qkc-success" role="status">
              <i className="fa-solid fa-circle-check" aria-hidden="true" />
              <span>Nice work! That's right.</span>
            </div>
          )}

          <p className="cc-qkc-card__count">Question 1 of 1</p>
          <h2 className="cc-qkc-card__question">{question.text}</h2>

          <div className="cc-qkc-options" role="radiogroup" aria-label="Answer choices">
            {question.options.map((option, index) => {
              const isSelected = selected === index;
              const isCorrectOption = question.correctIndex === index;
              const stateClass = submitted
                ? isSelected
                  ? isCorrectOption
                    ? " is-correct"
                    : " is-wrong"
                  : ""
                : isSelected
                  ? " is-selected"
                  : "";

              return (
                <button
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  className={`cc-qkc-option${stateClass}`}
                  key={option}
                  disabled={submitted}
                  onClick={() => setSelected(index)}
                >
                  <span className="cc-qkc-option__marker" aria-hidden="true">
                    {optionLetter(index)}
                  </span>
                  <span className="cc-qkc-option__text">{option}</span>
                </button>
              );
            })}
          </div>

          {showSuccess && (
            <div className="cc-qkc-explanation">
              <strong>Correct answer: {optionLetter(question.correctIndex)}</strong>
              <p>{question.explanation}</p>
            </div>
          )}

          <footer className={`cc-qkc-card__footer${showSuccess ? " cc-qkc-card__footer--success" : ""}`}>
            {!showSuccess && (
              <p>Pick the answer that best matches what the video described.</p>
            )}
            <div className="cc-qkc-card__actions">
              <button
                type="button"
                className="cc-qkc-action cc-qkc-action--back"
                onClick={onBack}
              >
                Go Back
              </button>
              {showSuccess && (
                <button
                  type="button"
                  className="cc-qkc-action cc-qkc-action--save"
                  onClick={() => onSaveLater(buildAnswersPayload())}
                >
                  Save and Return Later
                </button>
              )}
              <button
                type="button"
                className="cc-qkc-action cc-qkc-action--primary"
                onClick={showSuccess ? () => onCorrect(buildAnswersPayload()) : handleSubmit}
                disabled={!showSuccess && selected === null}
              >
                {showSuccess ? "Save and Continue" : "Submit Answer"}
              </button>
            </div>
          </footer>
        </div>
      </section>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CLASS-12  — Quick Knowledge Check (same overlay pattern, different question)
// ─────────────────────────────────────────────────────────────────────────────

const CLASS12_QUESTION = {
  text: "Of the four debt-relief options, which one can stop lawsuits, wage garnishments, and repossessions?",
  options: [
    "Debt consolidation",
    "Debt settlement",
    "A debt management plan",
    "Bankruptcy",
  ],
  correctIndex: 3, // D — Bankruptcy
  explanation:
    "That's right. Bankruptcy is the only option that triggers the automatic stay, the court order that halts lawsuits, garnishments, and repossessions. Nice work, let's keep going.",
};

// ─────────────────────────────────────────────────────────────────────────────
// CLASS-12 FORM — "Comparison of Options"
// ─────────────────────────────────────────────────────────────────────────────

const COMPARISON_ROWS = [
  {
    rowKey: "r1",
    question: "1. Can I realistically repay everything?",
    options: [
      "I can realistically repay everything (or almost everything) in the next two years.",
      "I cannot realistically repay everything in the next two years.",
    ],
    // c1 = Debt Consolidation, c2 = DMP, c3 = Debt Settlement, c4 = Bankruptcy
    alternativeDescriptions: [
      "Not a fit — consolidation requires full repayment.",
      "Not a fit — DMP requires full repayment.",
      "Possible fit — may reduce balances but unpredictable.",
      "Can be designed for situations when repayment is unrealistic.",
    ],
    descriptions: [
      {
        label: "Debt Consolidation",
        col: "c1",
        text: "Good fit if credit is fair-good and you qualify for a decent rate.",
      },
      {
        label: "Debt Management Plan (DMP)",
        col: "c2",
        text: "Good fit if you need lower interest + structure but can repay in full.",
      },
      {
        label: "Debt Settlement",
        col: "c3",
        text: "Sometimes fits if you can save lump sums, but not designed for full repayment.",
      },
      {
        label: "Bankruptcy",
        col: "c4",
        text: "Might be unnecessary if repayment is realistic.",
      },
    ],
  },
  {
    rowKey: "r2",
    question: "2. Do I need legal protection right now?",
    options: [
      "I do not need legal protection right now.",
      "I do need legal protection right now (lawsuits, garnishments, foreclosure, repossession).",
    ],
    alternativeDescriptions: [
      "Not a fit — no legal protections.",
      "Not a fit — no legal protections.",
      "Not a fit — no protection from lawsuits.",
      "Often the intended fit — automatic stay provides legal protection.",
    ],
    descriptions: [
      { label: "Debt Consolidation", col: "c1", text: "Can work if budget + credit qualify." },
      { label: "Debt Management Plan (DMP)", col: "c2", text: "Can work if creditors cooperate." },
      { label: "Debt Settlement", col: "c3", text: "Can work but may harm credit and involve tax issues." },
      { label: "Bankruptcy", col: "c4", text: "Possible, but may not be necessary." },
    ],
  },
  {
    rowKey: "r3",
    question: "3. How stable is my income?",
    options: [
      "My income is stable and predictable.",
      "My income is unstable or has dropped long-term.",
    ],
    alternativeDescriptions: [
      "Not a fit — missed payments can worsen debt.",
      "Not a fit — DMP requires exact monthly payments.",
      "Risky — settlement plans often collapse with unstable income.",
      "Often the intended fit — payment flexibility or debt discharge may help.",
    ],
    descriptions: [
      { label: "Debt Consolidation", col: "c1", text: "Good fit — requires reliable payments." },
      { label: "Debt Management Plan (DMP)", col: "c2", text: "Good fit — requires 3–5 years of steady payments." },
      { label: "Debt Settlement", col: "c3", text: "Possible fit — but requires saving lump sums consistently." },
      { label: "Bankruptcy", col: "c4", text: "Possible depending on other factors." },
    ],
  },
  {
    rowKey: "r4",
    question: "4. How long can I carry my stress?",
    options: [
      "I can carry this stress for several years if needed.",
      "I cannot carry this stress much longer.",
    ],
    alternativeDescriptions: [
      "Not a fit — long repayment timeline.",
      "Not a fit — long repayment timeline.",
      "Risky — stressful period before settlements occur.",
      "Often the fastest relief — Ch. 7 is 3–6 months, legal protections immediate.",
    ],
    descriptions: [
      { label: "Debt Consolidation", col: "c1", text: "Possible fit — usually 3–7 years." },
      { label: "Debt Management Plan (DMP)", col: "c2", text: "Possible fit — usually 3–5 years." },
      { label: "Debt Settlement", col: "c3", text: "Possible fit — may take 2–4 years or longer." },
      {
        label: "Bankruptcy",
        col: "c4",
        text: "Possible, though not always necessary if stress tolerance is high.",
      },
    ],
  },
];

const ATTORNEY_OPTIONS = [
  "Yes, please connect me with a neutral attorney referral service.",
  "No, I already have an attorney.",
  "No, I am filing pro se.",
];

/**
 * ComparisonOfOptionsForm
 * Shown after the Class-12 video.
 * API payload: { comparison: { r1:{c1,c2,c3,c4}, r2:{...}, r3:{...}, r4:{...} } }
 */
export const ComparisonOfOptionsForm = ({ onSubmit, onBack, onSaveLater, loading }) => {
  // Each row stores which RADIO (option index 0 or 1) is selected
  const [rowSelections, setRowSelections] = React.useState({ r1: null, r2: null, r3: null, r4: null });
  // Each column (c1–c4) checkbox per row
  const [colChecks, setColChecks]         = React.useState({
    r1: { c1: false, c2: false, c3: false, c4: false },
    r2: { c1: false, c2: false, c3: false, c4: false },
    r3: { c1: false, c2: false, c3: false, c4: false },
    r4: { c1: false, c2: false, c3: false, c4: false },
  });
  const [attorney, setAttorney] = React.useState("");
  const [attorneyAttempted, setAttorneyAttempted] = React.useState(false);
  const attorneyFieldsetRef = React.useRef(null);

  // Resolve answer-specific descriptions for the interactive table.
  const comparisonRows = COMPARISON_ROWS.map((row) => ({
    ...row,
    descriptions: row.descriptions.map((description, index) => ({
      ...description,
      text: rowSelections[row.rowKey] === null
        ? "Choose an answer to see this option."
        : rowSelections[row.rowKey] === 1
          ? row.alternativeDescriptions[index]
          : description.text,
    })),
  }));

  const selectAnswer = (rowKey, optionIndex) => {
    setRowSelections((previous) => ({ ...previous, [rowKey]: optionIndex }));
    // Checkmarks belong to the descriptions the user read, so clear this row only.
    setColChecks((previous) => ({
      ...previous,
      [rowKey]: { c1: false, c2: false, c3: false, c4: false },
    }));
  };

  // Tally counts
  const tally = React.useMemo(() => {
    const t = { c1: 0, c2: 0, c3: 0, c4: 0 };
    Object.values(colChecks).forEach((row) => {
      Object.keys(t).forEach((col) => { if (row[col]) t[col]++; });
    });
    return t;
  }, [colChecks]);

  const toggleCol = (rowKey, col) =>
    setColChecks((prev) => ({
      ...prev,
      [rowKey]: { ...prev[rowKey], [col]: !prev[rowKey][col] },
    }));

  const handleSubmit = () => {
    if (!attorney) {
      setAttorneyAttempted(true);
      window.requestAnimationFrame(() => {
        attorneyFieldsetRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        attorneyFieldsetRef.current?.querySelector("input")?.focus({ preventScroll: true });
      });
      return;
    }

    const comparison = {};
    COMPARISON_ROWS.forEach(({ rowKey }) => {
      comparison[rowKey] = {
        c1: colChecks[rowKey].c1 ? "checked" : "",
        c2: colChecks[rowKey].c2 ? "checked" : "",
        c3: colChecks[rowKey].c3 ? "checked" : "",
        c4: colChecks[rowKey].c4 ? "checked" : "",
      };
    });
    onSubmit({ comparison, attorney });
  };

  const colHeaders = [
    { col: "c1", label: "Debt Consolidation", icon: "fa-coins" },
    { col: "c2", label: "Debt Management Plan (DMP)", icon: "fa-chart-pie" },
    { col: "c3", label: "Debt Settlement", icon: "fa-scale-balanced" },
    { col: "c4", label: "Bankruptcy", icon: "fa-building-columns" },
  ];

  return (
    <div className="cc-comparison">
      {/* Page title */}
      <h1 className="cc-comparison__title">
        COMPARISON OF OPTIONS
      </h1>
      <p className="cc-comparison__intro">
        This section helps you compare consolidation, debt settlement, debt management plans, and
        bankruptcy based on your actual situation.
      </p>

      {/* Info bar */}
      <div className="cc-comparison__notice">
        <i className="fa-regular fa-circle-check" aria-hidden="true" />
        <p>
          For each question, choose the answer that best matches your life. Then read across the row and
          check any solution that matches the description in that box. When you finish, count how many
          checkmarks appear in each column. The column with the most marks may be the one that fits more of
          your needs — but only an attorney can give legal advice about bankruptcy, and your counselor cannot
          tell you which option to choose.
        </p>
      </div>

      {/* Comparison table */}
      <div className="cc-comparison-table-wrap" role="region" aria-label="Comparison of debt relief options" tabIndex="0">
        <table className="cc-comparison-table">
          <thead>
            <tr>
              <th scope="col">Question &amp; Your Answer</th>
              {colHeaders.map(({ col, label, icon }) => (
                <th scope="col" key={col}>
                  <span className={`cc-comparison-table__icon cc-comparison-table__icon--${col}`} aria-hidden="true">
                    <i className={`fa-solid ${icon}`} />
                  </span>
                  <span>{label}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {comparisonRows.map((row) => (
              <tr key={row.rowKey}>
                <th scope="row" className="cc-comparison-table__question">
                  <strong>{row.question}</strong>
                  {row.options.map((opt, optionIndex) => (
                    <label
                      className={`cc-comparison-radio${
                        rowSelections[row.rowKey] === optionIndex ? " is-selected" : ""
                      }`}
                      htmlFor={`${row.rowKey}_opt${optionIndex}`}
                      key={opt}
                    >
                      <input
                        type="radio"
                        name={`comparison_${row.rowKey}`}
                        id={`${row.rowKey}_opt${optionIndex}`}
                        checked={rowSelections[row.rowKey] === optionIndex}
                        onChange={() => selectAnswer(row.rowKey, optionIndex)}
                      />
                      <span>{opt}</span>
                    </label>
                  ))}
                </th>
                {row.descriptions.map(({ col, text }) => (
                  <td className={colChecks[row.rowKey][col] ? "is-checked" : ""} key={col}>
                    <label className="cc-comparison-table__choice">
                      <input
                        className="cc-comparison-checkbox"
                        disabled={rowSelections[row.rowKey] === null}
                        type="checkbox"
                        checked={colChecks[row.rowKey][col]}
                        onChange={() => toggleCol(row.rowKey, col)}
                      />
                      <span>{text}</span>
                    </label>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tally Section */}
      <section className="cc-comparison-tally">
        <h2>Tally Section</h2>
        <div className="cc-comparison-tally__grid">
          {colHeaders.map(({ col, label }) => (
            <div className="cc-comparison-tally__item" key={col}>
              <strong>{label}</strong>
              <span>{tally[col]}</span>
              <small>check marks</small>
            </div>
          ))}
        </div>
      </section>

      {/* Attorney referral */}
      <section className="cc-comparison-attorney">
        <p>
          If you would like help finding bankruptcy attorneys in your state, Evergreen can connect you by
          phone with a neutral nonprofit attorney referral service. The referral service can provide names of
          licensed attorneys in your area.
          <br />
          <em>
            Evergreen does not endorse any attorney, does not receive compensation for referrals, and cannot
            give legal advice.
          </em>
        </p>
        <fieldset
          className={`cc-comparison-attorney__options${attorneyAttempted && !attorney ? " has-error" : ""}`}
          ref={attorneyFieldsetRef}
          aria-invalid={attorneyAttempted && !attorney}
          aria-describedby={attorneyAttempted && !attorney ? "comparison-attorney-error" : undefined}
        >
          <legend>
            Would you like help finding a bankruptcy attorney?
            <span className="cc-required-mark" aria-hidden="true">*</span>
          </legend>
          {ATTORNEY_OPTIONS.map((opt, optionIndex) => (
            <label
              className={`cc-comparison-radio${attorney === opt ? " is-selected" : ""}`}
              htmlFor={`comparison_attorney_${optionIndex}`}
              key={opt}
            >
              <input
                type="radio"
                id={`comparison_attorney_${optionIndex}`}
                name="attorney"
                checked={attorney === opt}
                required
                onChange={() => {
                  setAttorney(opt);
                  setAttorneyAttempted(false);
                }}
              />
              <span>{opt}</span>
            </label>
          ))}
          {attorneyAttempted && !attorney && (
            <p className="cc-comparison-attorney__error" id="comparison-attorney-error" role="alert">
              Please select one option before continuing.
            </p>
          )}
        </fieldset>
      </section>

      {/* Footer buttons */}
      <div className="footer-form-btn d-flex flex-wrap mt-4">
        <button type="button" className="btn_brnd cc-action-back mt-4 me-3" onClick={onBack}>
          Back
        </button>
        <button type="button" className="btn_brnd cc-action-save-later mt-4 me-3" onClick={onSaveLater}>
          Save and Return Later
        </button>
        <a
          className="btn_brnd cc-comparison-download mt-4 me-3"
          href={`${process.env.PUBLIC_URL || ""}/downloads/comparison-of-options.pdf`}
          download="comparison-of-options.pdf"
        >
          <i className="fa-solid fa-download me-2" aria-hidden="true" />
          Download PDF
        </a>
        <button
          type="button"
          className="btn_brnd mt-4 btn-next"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? (
            <ThreeDots height="20" width="60" radius="9" color="#fff" ariaLabel="three-dots-loading" visible />
          ) : (
            "Save and continue"
          )}
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT  (CCLatestCourse)
// ─────────────────────────────────────────────────────────────────────────────
const ClassFiveSidebarItems = ({ activeSubClass, step, progress, onSelect, disabled, videoCompleted }) => {
  const [expanded, setExpanded] = useState({});
  return (
  <ul className="cc-course-subclasses" aria-label="Budget Analysis steps">
    {CLASS_FIVE_SIDEBAR_ITEMS.map((item) => {
      const isCurrent = item.subClass === activeSubClass;
      const isComplete = Boolean(progress?.[`aud5_${item.subClass}`]);
      const isFormStage = isCurrent && step === "class5form";
      const hasNestedForm = CLASS_FIVE_NESTED_FORM_SUBCLASSES.has(item.subClass);
      const isNestedFormStage = isFormStage && hasNestedForm;
      const showFormChild = hasNestedForm && (expanded[item.subClass] ?? isCurrent);
      const canOpen = isComplete || isCurrent;
      const canOpenForm = isComplete || (isCurrent && (videoCompleted || isFormStage));

      return (
        <React.Fragment key={item.subClass}>
          <li
            className={`cc-course-subclass${isCurrent && !isNestedFormStage ? " is-active" : ""}${isComplete || isNestedFormStage ? " is-complete" : ""}`}
            aria-current={isCurrent && !isNestedFormStage ? "step" : undefined}
            data-sidebar-active={isCurrent && !isNestedFormStage ? "true" : undefined}
          >
            {canOpen && <button type="button" className="cc-budget-nav-open" aria-label={`Open ${item.label}`} disabled={disabled} onClick={() => onSelect(item.subClass, "lesson")} />}
            <span className="cc-course-subclass__icon" aria-hidden="true">
              {isComplete || isNestedFormStage ? (
                <i className="fa-solid fa-check" />
              ) : CLASS_FIVE_VIDEO_SUBCLASSES.has(item.subClass) ? (
                <i className="fa-regular fa-circle-play" />
              ) : (
                <i className="fa-solid fa-bars-staggered" />
              )}
            </span>
            <span>{item.label}</span>
            {hasNestedForm && (
              <button type="button" className="cc-budget-nav-toggle" aria-label={`Show ${item.formLabel} form`} aria-expanded={showFormChild} disabled={!canOpen || disabled} onClick={() => setExpanded((current) => ({ ...current, [item.subClass]: !showFormChild }))}>
                <i className={`fa-solid ${showFormChild ? "fa-chevron-down" : "fa-chevron-right"}`} aria-hidden="true" />
              </button>
            )}
          </li>
          {showFormChild && (
            <li
              className={`cc-course-subclass-form${isFormStage ? " is-active" : ""}${isComplete ? " is-complete" : ""}`}
              aria-current={isFormStage ? "step" : undefined}
              data-sidebar-active={isFormStage ? "true" : undefined}
            >
              <button type="button" className="cc-budget-nav-open" aria-label={`Open ${item.formLabel} form`} disabled={!canOpenForm || disabled} onClick={() => onSelect(item.subClass, "class5form")} />
              <span className="cc-course-subclass-form__icon" aria-hidden="true">
                {isComplete ? <i className="fa-solid fa-check" /> : <i className="fa-solid fa-bars-staggered" />}
              </span>
              <span>{item.formLabel}</span>
            </li>
          )}
        </React.Fragment>
      );
    })}
  </ul>
);

};

const CCLatestCourse = ({
  thirdParty = false,
  token = null,
  clientId = null,
  params = null,
  client = null,
}) => {
  const location    = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const userId      = queryParams.get("id");
  const retake      = Boolean(
    queryParams.get("retake") &&
      queryParams.get("retake") !== "null" &&
      queryParams.get("retake") !== "false"
  );
  const user    = useSelector((state) => state.user);
  const isJoint = localStorage.getItem("isJoint");
  const navigate = useNavigate();

  const [getloading,    setGetLoading]    = useState(true);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [courseInfo,    setCourseInfo]    = useState(null);
  const [savedFormData, setSavedFormData] = useState({});
  const [activeTab,     setActiveTab]     = useState("transcript");
  const videoRef = useRef(null);
  const courseContentRef = useRef(null);
  const courseLoadingRef = useRef(null);
  const sidebarScrollRef = useRef(null);
  const assessmentSubmittingRef = useRef(false);
  const [contentLoading, setContentLoading] = useState(false);
  const [assessmentLoading, setAssessmentLoading] = useState(false);
  const [assessmentDataLoading, setAssessmentDataLoading] = useState(false);
  const [assessmentInitialData, setAssessmentInitialData] = useState(null);
  const [classFiveBudgetLoading, setClassFiveBudgetLoading] = useState(false);
  const [classFiveBudgetReady, setClassFiveBudgetReady] = useState(false);
  const [budgetFormAction, setBudgetFormAction] = useState(null);
  const [videoCompleted, setVideoCompleted] = useState(false);
  const [classFiveReport, setClassFiveReport] = useState(null);
  const [reportReferral, setReportReferral] = useState("no");
  const [reportReferralAttempted, setReportReferralAttempted] = useState(false);
  const [savedForLater, setSavedForLater] = useState(false);
  const reportReferralRef = useRef(null);

  const gladeRequest = async (endpoint, body = {}) => {
    const paramsQuery = params ? `?params=${encodeURIComponent(params)}` : "";
    const response = await fetch(buildApiUrl(`api/${endpoint}${paramsQuery}`), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        API_key: process.env.REACT_APP_API_KEY,
        clientId,
        ...body,
      }),
    });
    const data = await response.json();
    if (!response.ok) {
      const error = new Error(data?.error || data?.message || "Request failed");
      error.response = { data, status: response.status };
      throw error;
    }
    return { data };
  };

  // ── Flow state ──────────────────────────────────────────────────────────────
  // step:
  //   "lesson"   – watching video / reading
  //   "quiz"     – quiz page (classes that have a quiz)
  //   "form"     – comparison form (class 12)
  //   "qkc"      – standalone Quick Knowledge Check (class 7, class 12)
  //   "class6form"  – standalone How I Got Here form (class 6, no quiz)
  const [step,              setStep]              = useState("lesson");
  const [pendingFormValues, setPendingFormValues] = useState(null);
  const [expenseFormStep,   setExpenseFormStep]   = useState(1);
  const [assetFormStep,     setAssetFormStep]     = useState(1);

  const LESSON_TITLES = [
    { rank: 1,  title: "Welcome and Purpose" },
    { rank: 2,  title: "Important Disclosures" },
    { rank: 3,  title: "Replacing Regret with Wisdom" },
    { rank: 4,  title: "Preparing for Budget Analysis" },
    { rank: 5,  title: "Budget Analysis" },
    { rank: 6,  title: "How You Got Here" },
    { rank: 7,  title: "Problem Solving" },
    { rank: 8,  title: "Debt Consolidation" },
    { rank: 9,  title: "Debt Settlement and Negotiation" },
    { rank: 10, title: "Debt Management Plans" },
    { rank: 11, title: "Bankruptcy" },
    { rank: 12, title: "A Comparison" },
    { rank: 13, title: "Improving Your Credit" },
    { rank: 14, title: "Completing Your Session" },
  ];

  async function fetchCourseInfo(viewTarget = null, requestedStep = null) {
    try {
      const response = thirdParty
        ? await gladeRequest("course_first_new_data", viewTarget || {})
        : await latestcourseData(
            userId ? "admin" : "user",
            userId ? userId : user.user.data.id,
            isJoint == "true" ? "true" : "false",
            retake ? 1 : 0,
            viewTarget || {}
          );
      const responseData = response.data || {};
      const isCompletedResponse = isCourseCompletionPayload(responseData);

      if (isCompletedResponse) {
        setCourseInfo(responseData);
        setGetLoading(false);
        setUpdateLoading(false);
        setContentLoading(false);
        return true;
      }

      const backendContent = responseData.contentData || {};
      const classNumber = Number(backendContent.class);
      const subClassNumber = Number(
        backendContent.subClass || backendContent.sub_class
      );
      const frontendContent = classNumber === 5
        ? ccClassFiveContent.subclasses[String(subClassNumber)]
        : ccCourseContent.lessons[String(classNumber)];

      if (classNumber === 5 && subClassNumber) {
        const courseId = response.data.data?.id || response.data.data?.ID;
        const draftKey = `cc-class5-draft:${thirdParty ? clientId : (userId || user.user.data.id)}:${courseId}:${subClassNumber}`;
        try {
          const draft = JSON.parse(sessionStorage.getItem(draftKey));
          if (draft) {
            setSavedFormData((previous) => ({
              ...previous,
              [`5-${subClassNumber}`]: draft,
            }));
          }
        } catch {
          sessionStorage.removeItem(draftKey);
        }
      }

      setCourseInfo({
        ...responseData,
        contentData: {
          ...backendContent,
          ...(classNumber === 2 ? { video: null } : {}),
          requestedStep,
          ...(frontendContent
            ? {
                lessontitle: frontendContent.title,
                text: null,
                readMore: null,
                formName: classNumber === 5 ? frontendContent.formName : null,
                frontendContent,
              }
            : {}),
          lessons: LESSON_TITLES,
        },
      });
      setGetLoading(false);
      setUpdateLoading(false);
      setContentLoading(false);
      return true;
    } catch (error) {
      setGetLoading(false);
      setUpdateLoading(false);
      setContentLoading(false);
      if (viewTarget) {
        toast.error(
          error?.response?.data?.error ||
          "Could not open the previous course section."
        );
      }
      return false;
    }
  }

  useEffect(() => { fetchCourseInfo(); }, []);

  // Reset flow when lesson changes
  useEffect(() => {
    if (videoRef.current) videoRef.current.load();
    setActiveTab("transcript");
    const currentClass = Number(courseInfo?.contentData?.class);
    const currentSubClass = Number(
      courseInfo?.contentData?.subClass || courseInfo?.contentData?.sub_class
    );
    const currentFormName = courseInfo?.contentData?.formName;
    const startsOnClassFiveForm =
      currentClass === 5 &&
      Boolean(currentFormName) &&
      !CLASS_FIVE_VIDEO_SUBCLASSES.has(currentSubClass);
    setStep(startsOnClassFiveForm || courseInfo?.contentData?.requestedStep === "class5form" ? "class5form" : "lesson");
    setPendingFormValues(null);
    setVideoCompleted(false);
    setExpenseFormStep(1);
    setAssetFormStep(1);
  }, [
    courseInfo?.contentData?.class,
    courseInfo?.contentData?.subClass,
    courseInfo?.contentData?.sub_class,
    courseInfo?.contentData?.formName,
    courseInfo?.contentData?.requestedStep,
  ]);

  const rank     = Number(courseInfo?.contentData?.class) || 0;
  const isCourseCompleted = isCourseCompletionPayload(courseInfo);
  const courseCompletionReturn = getCourseCompletionReturn(userId);
  const subClass = Number(
    courseInfo?.contentData?.subClass || courseInfo?.contentData?.sub_class
  ) || 0;
  const currentClassTitle = LESSON_TITLES.find((lesson) => lesson.rank === rank)?.title
    || courseInfo?.contentData?.lessontitle
    || "Current class";
  const classFiveProgress = courseInfo?.subclassData || {};
  const completedLessonCount = LESSON_TITLES.filter(
    (lesson) => Boolean(courseInfo?.data?.[`aud${lesson.rank}`])
  ).length;
  const frontendLesson = courseInfo?.contentData?.frontendContent || null;
  const formName = rank === 5 ? courseInfo?.contentData?.formName || null : null;
  const formConfig = formName ? FORM_MAP[formName] : null;
  const isClassFiveVideoStep = rank === 5 && CLASS_FIVE_VIDEO_SUBCLASSES.has(subClass);
  const isClassFiveReport = rank === 5 && subClass === CLASS_FIVE_SUBCLASS_COUNT;
  const isVideoContinueLocked = (Boolean(courseInfo?.contentData?.video) || isClassFiveVideoStep) && !videoCompleted;
  const formStorageKey = rank === 5 ? `${rank}-${subClass}` : `${rank}`;
  const savedValues = formName === "monthly_income"
    ? prepareClassFiveFormValues(formName, savedFormData[formStorageKey] || {})
    : savedFormData[formStorageKey] || {};
  const formInitialValues = formConfig
    ? {
        ...formConfig.initialValues,
        ...savedValues,
        ...(formConfig.initialValues.person2
          ? { person2: { ...formConfig.initialValues.person2, ...(savedValues.person2 || {}) } }
          : {}),
        ...(formConfig.initialValues.securedDraft
          ? { securedDraft: { ...formConfig.initialValues.securedDraft, ...(savedValues.securedDraft || {}) } }
          : {}),
        ...(formConfig.initialValues.unsecuredDraft
          ? { unsecuredDraft: { ...formConfig.initialValues.unsecuredDraft, ...(savedValues.unsecuredDraft || {}) } }
          : {}),
        ...(formConfig.initialValues.vehicleDraft
          ? { vehicleDraft: { ...formConfig.initialValues.vehicleDraft, ...(savedValues.vehicleDraft || {}) } }
          : {}),
        ...(formConfig.initialValues.propertyDraft
          ? { propertyDraft: { ...formConfig.initialValues.propertyDraft, ...(savedValues.propertyDraft || {}) } }
          : {}),
        ...(formConfig.initialValues.otherAssetDraft
          ? { otherAssetDraft: { ...formConfig.initialValues.otherAssetDraft, ...(savedValues.otherAssetDraft || {}) } }
          : {}),
      }
    : null;
  const formSchema = formConfig ? formConfig.buildSchema() : null;
  const isMonthlyExpensesForm = rank === 5 && formName === "monthly_expenses";
  const isAssetsForm = rank === 5 && formName === "assets";
  const isClassFiveMultiStepForm = isMonthlyExpensesForm || isAssetsForm;
  const isBudgetFormActionLoading = Boolean(budgetFormAction);
  const isFinalMultiStepForm =
    (!isMonthlyExpensesForm || expenseFormStep === EXPENSE_STEPS.length) &&
    (!isAssetsForm || assetFormStep === 5);

  // Hide the left lesson sidebar when showing a form (except class 4),
  // or when showing the standalone class-6 form, or when a lesson contains a Formik form.
  const showSidebar = !(
    (step === "form" && rank !== 4) ||
    step === "class6form" ||
    (step === "lesson" && formConfig && rank !== 5)
  );

  // Keep the active class visible without resetting the list when switching steps.
  useEffect(() => {
    if (!showSidebar) return undefined;

    const frameId = window.requestAnimationFrame(() => {
      const scrollContainer = sidebarScrollRef.current;
      if (!scrollContainer) return;

      const activeItems = scrollContainer.querySelectorAll('[data-sidebar-active="true"]');
      const activeItem = activeItems[activeItems.length - 1];
      if (!activeItem) return;

      const containerRect = scrollContainer.getBoundingClientRect();
      const activeRect = activeItem.getBoundingClientRect();
      // Preserve the list position if the active lesson is already visible.
      if (activeRect.top >= containerRect.top && activeRect.bottom <= containerRect.bottom) return;
      const targetTop = Math.max(0, scrollContainer.scrollTop + (
        activeRect.top < containerRect.top
          ? activeRect.top - containerRect.top - 8
          : activeRect.bottom - containerRect.bottom + 8
      ));
      const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

      scrollContainer.scrollTo({
        top: targetTop,
        behavior: reduceMotion ? "auto" : "smooth",
      });
    });

    return () => window.cancelAnimationFrame(frameId);
  }, [rank, subClass, step, showSidebar]);

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const apiCallUser = thirdParty ? "glade" : (userId ? "admin" : "user");
  const apiCallId   = thirdParty ? clientId : (userId ? userId : user.user.data.id);
  const apiJoint    = isJoint == "true" ? "true" : "false";
  const apiRetake   = retake ? 1 : 0;
  const classFiveCourseId = courseInfo?.data?.id || courseInfo?.data?.ID;
  const classFiveDraftKey = rank === 5 && subClass
    ? `cc-class5-draft:${apiCallId}:${classFiveCourseId}:${subClass}`
    : null;
  const loadAssessment = () => thirdParty
    ? gladeRequest("get_user_assessment")
    : getUserAssessment(apiCallUser, apiCallId);
  const saveAssessment = (values) => thirdParty
    ? gladeRequest("user_assessment", values)
    : userAssessment(apiCallUser, apiCallId, values);
  const loadClassFiveBudget = () => thirdParty
    ? gladeRequest("class_five_budget")
    : getClassFiveBudget(apiCallUser, apiCallId);
  const persistClassFiveBudget = (section, values) => thirdParty
    ? gladeRequest(`class_five_budget/${encodeURIComponent(section)}`, values)
    : saveClassFiveBudget(apiCallUser, apiCallId, section, values);
  const sendClassFiveBudgetReport = (values) => thirdParty
    ? gladeRequest("class_five_budget/report-email", values)
    : emailClassFiveBudgetReport(apiCallUser, apiCallId, values);
  const persistCourseUpdate = (values) => thirdParty
    ? gladeRequest("course_first_new_updation", values)
    : latestcourseUpdate(apiCallUser, apiCallId, values, apiJoint, apiRetake);
  const persistQuiz = (values) => thirdParty
    ? gladeRequest("course_first_quiz", values)
    : saveQuizFirst(apiCallUser, apiCallId, values, apiJoint);

  const focusCourseTop = useCallback((behavior = "smooth") => {
    document.activeElement?.blur?.();
    window.scrollTo({ top: 0, left: 0, behavior });

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior });
      window.requestAnimationFrame(() => {
        (courseLoadingRef.current || courseContentRef.current)?.focus({ preventScroll: true });
        window.scrollTo({ top: 0, left: 0, behavior });
      });
    });
  }, []);

  // Focus the next successful course view after React commits it.
  useEffect(() => {
    if (!courseInfo || getloading) return;
    document.activeElement?.blur?.();
    focusCourseTop("auto");
  }, [
    rank,
    subClass,
    step,
    expenseFormStep,
    assetFormStep,
    getloading,
    courseInfo,
    focusCourseTop,
  ]);

  useEffect(() => {
    if (rank !== 6) {
      setAssessmentInitialData(null);
      setAssessmentDataLoading(false);
      return undefined;
    }

    let cancelled = false;
    setAssessmentDataLoading(true);
    loadAssessment()
      .then((response) => {
        if (!cancelled) {
          setAssessmentInitialData(response.data?.data || {});
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setAssessmentInitialData({});
          toast.error(error?.response?.data?.error || "Could not load saved assessment answers.");
        }
      })
      .finally(() => {
        if (!cancelled) setAssessmentDataLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [apiCallId, apiCallUser, rank]);

  useEffect(() => {
    if (rank !== 5 || !classFiveCourseId) return undefined;

    let cancelled = false;
    setClassFiveBudgetReady(false);
    setClassFiveBudgetLoading(true);
    loadClassFiveBudget()
      .then((response) => {
        if (cancelled) return;
        const forms = response.data?.data?.forms || {};
        setClassFiveReport(response.data?.data?.report || null);
        const serverValues = Object.entries(CLASS_FIVE_FORM_SUBCLASSES).reduce(
          (result, [name, formSubClass]) => ({
            ...result,
            [`5-${formSubClass}`]: prepareClassFiveFormValues(name, forms[name] || {}),
          }),
          {}
        );
        serverValues["5-9"] = forms.report || {};
        setSavedFormData((previous) => {
          const drafts = {};
          Object.entries(CLASS_FIVE_FORM_SUBCLASSES).forEach(([draftFormName, formSubClass]) => {
            try {
              const draft = JSON.parse(sessionStorage.getItem(
                `cc-class5-draft:${apiCallId}:${classFiveCourseId}:${formSubClass}`
              ));
              if (draft) {
                const preparedDraft = prepareClassFiveFormValues(draftFormName, draft);
                drafts[`5-${formSubClass}`] = preparedDraft;
                sessionStorage.setItem(
                  `cc-class5-draft:${apiCallId}:${classFiveCourseId}:${formSubClass}`,
                  JSON.stringify(preparedDraft)
                );
              }
            } catch {
              sessionStorage.removeItem(
                `cc-class5-draft:${apiCallId}:${classFiveCourseId}:${formSubClass}`
              );
            }
          });
          return { ...previous, ...serverValues, ...drafts };
        });
        if (forms.report?.referral) setReportReferral(forms.report.referral);
        setClassFiveBudgetReady(true);
      })
      .catch((error) => {
        if (!cancelled) {
          toast.error(error?.response?.data?.error || "Could not load saved budget information.");
        }
      })
      .finally(() => {
        if (!cancelled) setClassFiveBudgetLoading(false);
      });

    return () => { cancelled = true; };
  }, [apiCallId, apiCallUser, classFiveCourseId, rank]);

  useEffect(() => {
    if (!isClassFiveReport || !classFiveDraftKey) {
      setReportReferral("no");
      setReportReferralAttempted(false);
      return;
    }

    try {
      const draft = JSON.parse(sessionStorage.getItem(classFiveDraftKey));
      setReportReferral(draft?.referral || savedFormData["5-9"]?.referral || "no");
    } catch {
      sessionStorage.removeItem(classFiveDraftKey);
      setReportReferral(savedFormData["5-9"]?.referral || "no");
    }
    setReportReferralAttempted(false);
  }, [classFiveDraftKey, isClassFiveReport, savedFormData]);

  const readClassFiveDraft = (draftSubClass) => {
    const memoryDraft = savedFormData[`5-${draftSubClass}`];
    if (memoryDraft) return memoryDraft;
    try {
      return JSON.parse(
        sessionStorage.getItem(`cc-class5-draft:${apiCallId}:${classFiveCourseId}:${draftSubClass}`)
      ) || {};
    } catch {
      return {};
    }
  };
  const toAmount = (value) => {
    const amount = Number.parseFloat(value);
    return Number.isFinite(amount) ? amount : 0;
  };
  const incomeDraft = readClassFiveDraft(3);
  const expenseDraft = readClassFiveDraft(4);
  const debtsDraft = readClassFiveDraft(5);
  const netIncomeFields = INCOME_FIELDS.filter(
    (field) => field !== "Gross monthly income from work (before taxes)"
  );
  const sumIncome = (person = {}) => netIncomeFields.reduce(
    (total, field) => total + toAmount(person[field]),
    0
  );
  const totalNetIncome = sumIncome(incomeDraft) + sumIncome(incomeDraft.person2);
  const totalLivingExpenses = EXPENSE_SECTIONS.reduce(
    (total, section) => total + section.fields.reduce(
      (sectionTotal, field) => sectionTotal + toAmount(expenseDraft[field]),
      0
    ),
    0
  );
  const securedDebts = Array.isArray(debtsDraft.secured) ? debtsDraft.secured : [];
  const unsecuredDebts = Array.isArray(debtsDraft.unsecured) ? debtsDraft.unsecured : [];
  const sumDebtField = (rows, field) => rows.reduce(
    (total, debt) => total + toAmount(debt?.[field]),
    0
  );
  const securedPayments = sumDebtField(securedDebts, "payment");
  const unsecuredPayments = sumDebtField(unsecuredDebts, "payment");
  const reviewSummary = {
    netIncome: totalNetIncome,
    livingExpenses: totalLivingExpenses,
    securedPayments,
    unsecuredPayments,
    surplus: totalNetIncome - totalLivingExpenses - securedPayments - unsecuredPayments,
    unsecuredBalance: sumDebtField(unsecuredDebts, "balance"),
    securedBalance: sumDebtField(securedDebts, "balance"),
  };

  /** Submit an assessment payload to user/user_assessment then advance */
  const submitAssessment = async (extraPayload) => {
    if (assessmentSubmittingRef.current) return false;

    assessmentSubmittingRef.current = true;
    setAssessmentLoading(true);
    try {
      await saveAssessment({
        userid: apiCallId,
        ...extraPayload,
      });
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not save assessment.");
      return false;
    } finally {
      assessmentSubmittingRef.current = false;
      setAssessmentLoading(false);
    }
  };

  /** Mark the lesson complete and move to the next one */
  const advanceLesson = async (
    formValues = null,
    redirectToDashboard = false,
    { inlineLoading = false } = {}
  ) => {
    if (!inlineLoading) {
      setUpdateLoading(true);
      setContentLoading(true);
    }
    const payload = {
      video: `${rank}`,
      ...(rank === 5 ? { subClass } : {}),
      ...(formValues && rank !== 5 ? { formData: formValues } : {}),
    };
    try {
      const updateResponse = await persistCourseUpdate(payload);
      const updateData = updateResponse?.data || {};
      if (rank === 5 && subClass === CLASS_FIVE_SUBCLASS_COUNT) {
        for (let draftSubClass = 1; draftSubClass <= CLASS_FIVE_SUBCLASS_COUNT; draftSubClass += 1) {
          sessionStorage.removeItem(
            `cc-class5-draft:${apiCallId}:${classFiveCourseId}:${draftSubClass}`
          );
        }
      }
      if (redirectToDashboard) { navigate("/dashboard"); return true; }
      if (thirdParty && isCourseCompletionPayload(updateData)) {
        setCourseInfo(updateData);
        return true;
      }
      if (rank === TOTAL_LESSONS) {
        if (!thirdParty) navigate(`/creditcounseling?course=first&retake=${retake}`);
      } else {
        await fetchCourseInfo();
      }
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.error);
      return false;
    } finally {
      if (!inlineLoading) {
        setUpdateLoading(false);
        setContentLoading(false);
      }
    }
  };

  const handleClassFiveReportContinue = async (selectedRecommendations = []) => {
    if (!reportReferral) {
      setReportReferralAttempted(true);
      window.requestAnimationFrame(() => {
        reportReferralRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      });
      return;
    }

    if (!classFiveBudgetReady) {
      toast.error("Please wait until your saved budget information has loaded.");
      return;
    }

    focusCourseTop("auto");
    try {
      setUpdateLoading(true);
      const response = await persistClassFiveBudget("report", { referral: reportReferral });
      setClassFiveReport(response.data?.data?.report || classFiveReport);
      if (thirdParty) {
        try {
          await sendClassFiveBudgetReport({ selectedRecommendations });
        } catch (emailError) {
          // Email is optional for Glade. A rejected mailbox or mail-service
          // outage must not prevent the learner from continuing the course.
          console.warn("Glade budget report email delivery was unavailable.", emailError);
        }
      } else {
        await sendClassFiveBudgetReport({ selectedRecommendations });
      }
      await advanceLesson(null, false, { inlineLoading: true });
    } catch (error) {
      toast.error(error?.response?.data?.error || "Could not email your budget report.");
    } finally {
      setUpdateLoading(false);
    }
  };

  // ── Video end handler ────────────────────────────────────────────────────────
  const handleVideoEnd = async () => {
    if (rank === 5) {
      if (formConfig) {
        // Move away from the video action area immediately, then restore focus
        // after React has committed the budget form to avoid scroll anchoring.
        focusCourseTop("auto");
        setStep("class5form");
        window.requestAnimationFrame(() => focusCourseTop("auto"));
        return;
      }
      if (isClassFiveReport) {
        if (!classFiveBudgetReady) {
          toast.error("Please wait until your saved budget information has loaded.");
          return;
        }
        focusCourseTop("auto");
        try {
          setUpdateLoading(true);
          const response = await loadClassFiveBudget();
          const report = response.data?.data?.report;
          if (!report) throw new Error("Budget report data was not returned");
          setClassFiveReport(report);
          const savedReferral = response.data?.data?.forms?.report?.referral;
          if (savedReferral) setReportReferral(savedReferral);
          setVideoCompleted(true);
          setStep("class5report");
          setUpdateLoading(false);
          focusCourseTop("auto");
          return;
        } catch (error) {
          setUpdateLoading(false);
          toast.error(error?.response?.data?.error || "Could not prepare your budget report.");
          return;
        }
      }
      // Bring the content loader into view when leaving a Class 5 video.
      focusCourseTop("auto");
      advanceLesson();
      return;
    }
    if (rank === TOTAL_LESSONS) {
      // Keep the completion loader and the destination view at the top.
      focusCourseTop("auto");
      const didComplete = await advanceLesson();
      if (didComplete) focusCourseTop("auto");
      return;
    }
    if (rank === 6)  { setStep("class6form"); return; }  // class 6 → direct form
    if (rank === 7)  { setStep("qkc"); return; }
    if (rank === 12) { setStep("form"); return; }

    // classes with a QUIZ_DATA entry → show quiz
    if (QUIZ_DATA[rank]) { setStep("quiz"); setPendingFormValues(null);
     
      return; }

    // classes with a Formik form (existing forms) → handled via Formik onSubmit
    // For pure video classes with no quiz and no form → advance directly
    if (!formConfig) { advanceLesson(); }
  };

  // ── After quiz "Save and Continue" ──────────────────────────────────────────
  const persistQuizAnswers = async (answers) => {
    // quiz_first references course_first; retake courses use a separate table.
    if (apiRetake === 1) return;
    if (!classFiveCourseId) throw new Error("Course Id Not Found");

    await persistQuiz({
      courseId: classFiveCourseId,
      class_no: rank,
      answers,
    });
  };

  const handleQuizComplete = async (answers) => {
    try {
      setUpdateLoading(true);
      await persistQuizAnswers(answers);
      // Completing the recap also completes Class 4; its checklist is a download.
      // Other quizzed classes use the same lesson completion path.
      await advanceLesson(pendingFormValues);
    } catch (error) {
      setUpdateLoading(false);
      toast.error(error?.response?.data?.error || error.message || "Could not save quiz answers.");
    }
  };

  // ── Existing class-5 budget form submit (budget branch intentionally unchanged) ──
  const handleFormikSubmit = async (values) => {
    const preparedValues = rank === 5
      ? prepareClassFiveFormValues(formName, values)
      : values;
    if (preparedValues) {
      setSavedFormData((prev) => ({ ...prev, [formStorageKey]: preparedValues }));
      if (rank === 5 && classFiveDraftKey) {
        sessionStorage.setItem(classFiveDraftKey, JSON.stringify(preparedValues));
      }
    }
    setPendingFormValues(preparedValues);
    if (rank === 5) {
      if (!classFiveBudgetReady) {
        toast.error("Please wait until your saved budget information has loaded.");
        return;
      }
      try {
        setBudgetFormAction("save-continue");
        await persistClassFiveBudget(formName, preparedValues);
      } catch (error) {
        setBudgetFormAction(null);
        toast.error(error?.response?.data?.error || "Could not save this budget section.");
        return;
      }
      if (formName === "debts") {
        setBudgetFormAction(null);
        setStep("quiz");
        focusCourseTop();
        return;
      }
      await advanceLesson(null, false, { inlineLoading: true });
      setBudgetFormAction(null);
      return;
    }
    // If this class has a quiz → show quiz first, quiz will advance
    if (QUIZ_DATA[rank]) { setStep("quiz"); return; }
    advanceLesson(preparedValues);
  };

  const moveToNextBudgetFormStep = () => {
    document.activeElement?.blur?.();
    if (isMonthlyExpensesForm) {
      setExpenseFormStep((current) => Math.min(current + 1, EXPENSE_STEPS.length));
    } else if (isAssetsForm) {
      setAssetFormStep((current) => Math.min(current + 1, 5));
    }
    focusCourseTop();
  };

  const handleStepSaveAndContinue = async (values) => {
    if (!classFiveBudgetReady) {
      toast.error("Please wait until your saved budget information has loaded.");
      return;
    }

    const preparedValues = prepareClassFiveFormValues(formName, values);
    setSavedFormData((previous) => ({ ...previous, [formStorageKey]: preparedValues }));
    if (classFiveDraftKey) {
      sessionStorage.setItem(classFiveDraftKey, JSON.stringify(preparedValues));
    }

    try {
      setBudgetFormAction("save-continue");
      await persistClassFiveBudget(formName, preparedValues);
      moveToNextBudgetFormStep();
    } catch (error) {
      toast.error(error?.response?.data?.error || "Could not save this budget section.");
    } finally {
      setBudgetFormAction(null);
    }
  };

  // ── Class-6 HowGotHere form submit ──────────────────────────────────────────
  const handleHowGotHereSubmit = async (payload) => {
    const didStart = await submitAssessment(payload);
    if (!didStart) return;

    // The assessment save has completed. Move to the top before starting the
    // next course-content request so its loading state is visible immediately.
    focusCourseTop("auto");
    await new Promise((resolve) => window.requestAnimationFrame(resolve));

    const didAdvance = await advanceLesson();
    if (didAdvance) focusCourseTop("auto");
  };

  // ── Class-12 ComparisonOfOptions form submit ─────────────────────────────────
  const handleComparisonSubmit = async (payload) => {
    const didStart = await submitAssessment(payload);
    if (!didStart) return;
    setStep("qkc");
    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
  };

  // ── Class-7 / Class-12 QKC correct ──────────────────────────────────────────
  const handleQKCCorrect = async (answers) => {
    focusCourseTop("auto");
    try {
      setUpdateLoading(true);
      await persistQuizAnswers(answers);
      await advanceLesson();
      focusCourseTop("auto");
    } catch (error) {
      setUpdateLoading(false);
      toast.error(error?.response?.data?.error || error.message || "Could not save quiz answers.");
    }
  };

  const handleBack = async () => {
    if (rank <= 1 || contentLoading || updateLoading) return;

    const viewTarget = rank === 5 && subClass > 1
      ? { viewClass: 5, viewSubClass: subClass - 1 }
      : {
          viewClass: rank - 1,
          ...(rank - 1 === 5
            ? { viewSubClass: CLASS_FIVE_SUBCLASS_COUNT }
            : {}),
        };

    focusCourseTop("auto");
    setContentLoading(true);
    await fetchCourseInfo(viewTarget);
  };

  const handleBudgetSelect = async (targetSubClass, targetStep) => {
    if (contentLoading || updateLoading || assessmentLoading || classFiveBudgetLoading || budgetFormAction) return;
    const completed = Boolean(classFiveProgress[`aud5_${targetSubClass}`]);
    if (targetSubClass === subClass) {
      if (targetStep === "class5form" && !completed && !videoCompleted && step !== "class5form") return;
      setStep(CLASS_FIVE_VIDEO_SUBCLASSES.has(targetSubClass) ? targetStep : "class5form");
      focusCourseTop("auto");
      return;
    }
    if (!completed) return;
    focusCourseTop("auto");
    setContentLoading(true);
    await fetchCourseInfo({ viewClass: 5, viewSubClass: targetSubClass }, targetStep);
  };

  const handleLessonSelect = async (lessonRank) => {
    if (contentLoading || updateLoading || assessmentLoading || classFiveBudgetLoading || budgetFormAction) return;
    if (!courseInfo?.data?.[`aud${lessonRank}`]) return;
    focusCourseTop("auto");
    setContentLoading(true);
    await fetchCourseInfo({
      viewClass: lessonRank,
      ...(lessonRank === 5 ? { viewSubClass: 1 } : {}),
    });
  };
  const handleSaveLater = async (values = null) => {
    const showInlineBudgetLoading = rank === 5 && Boolean(formName) && values !== null;
    if (rank === 5 && classFiveDraftKey) {
      const draftValues = isClassFiveReport
        ? { referral: reportReferral }
        : values === null
          ? null
          : prepareClassFiveFormValues(formName, values);
      if (draftValues) {
        sessionStorage.setItem(classFiveDraftKey, JSON.stringify(draftValues));
      }
      const section = isClassFiveReport ? "report" : formName;
      if (section && draftValues) {
        if (!classFiveBudgetReady) {
          toast.error("Please wait until your saved budget information has loaded.");
          return;
        }
        try {
          if (showInlineBudgetLoading) setBudgetFormAction("save-later");
          else setUpdateLoading(true);
          await persistClassFiveBudget(section, draftValues);
        } catch (error) {
          setUpdateLoading(false);
          setBudgetFormAction(null);
          toast.error(error?.response?.data?.error || "Could not save this budget section.");
          return;
        }
      }
    }
    setBudgetFormAction(null);
    if (thirdParty) setSavedForLater(true);
    else navigate("/dashboard");
  };

  const handleQuizSaveLater = async (answers) => {
    if (!answers) {
      if (thirdParty) setSavedForLater(true);
      else navigate("/dashboard");
      return;
    }
    try {
      setUpdateLoading(true);
      await persistQuizAnswers(answers);
      if (thirdParty) setSavedForLater(true);
      else navigate("/dashboard");
    } catch (error) {
      setUpdateLoading(false);
      toast.error(error?.response?.data?.error || error.message || "Could not save quiz answers.");
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className={`cc-course-page${thirdParty ? " cc-course-page--third-party" : ""}`}>
      {!thirdParty && <Header />}
      {thirdParty && <ClientIdentityBadge client={client} />}

      {savedForLater ? (
        <main className="theme_container cc-course-complete">
          <div className="cc-course-complete__card" role="status">
            <span className="cc-course-complete__icon" aria-hidden="true">
              <i className="fa-solid fa-circle-check" />
            </span>
            <h1>Progress Saved</h1>
            <p>You can close this page and use the same Glade course link when you are ready to continue.</p>
            <button type="button" className="btn_brnd" onClick={() => setSavedForLater(false)}>
              Return to Course
            </button>
          </div>
        </main>
      ) : getloading ? (
        <div className="cc-course-loading d-flex justify-content-center">
          <ThreeDots height="40" width="80" radius="9" color="#4fa94d" ariaLabel="three-dots-loading"
            wrapperStyle={{ display: "flex", justifyContent: "center" }} visible />
        </div>
      ) : isCourseCompleted ? (
        <main
          ref={courseContentRef}
          tabIndex={-1}
          className="theme_container cc-course-complete"
        >
          <div className="cc-course-complete__card" role="status">
            <span className="cc-course-complete__icon" aria-hidden="true">
              <i className="fa-solid fa-circle-check" />
            </span>
            <h1>Course Completed</h1>
            <p>You have successfully completed Pre-Filing Credit Counseling.</p>
            {!thirdParty && (
              <button
                type="button"
                className="btn_brnd"
                onClick={() => navigate(courseCompletionReturn.path)}
              >
                {courseCompletionReturn.label}
              </button>
            )}
          </div>
        </main>
      ) : courseInfo?.contentData ? (
        <main className={`theme_container course_container cc-course-container${showSidebar ? " cc-course-container--with-sidebar" : ""}${rank === 5 ? " cc-course-container--class-five" : ""}${step === "form" && rank === 12 ? " cc-course-container--comparison" : ""}${step === "class5form" && formName === "debts" ? " cc-course-container--debts" : ""}${step === "class5form" && ["monthly_income", "monthly_expenses"].includes(formName) ? " cc-course-container--monthly-budget" : ""}`}>
          <nav className="cc-course-breadcrumb" aria-label="Breadcrumb">
            <span>Courses</span>
            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
            <strong>Pre-Filing Credit Counseling</strong>
            <i className="fa-solid fa-chevron-right" aria-hidden="true" />
            <span className="cc-course-breadcrumb__class" aria-current="page">
              Class {rank}: {currentClassTitle}
            </span>
          </nav>

          <div className="course-list-video">
            <div className={`cc-course-layout ${showSidebar ? "" : "cc-course-layout--wide"}${step === "lesson" && courseInfo?.contentData?.video ? " cc-course-layout--video" : ""}`}>

              {/* ── Sidebar ── */}
              {showSidebar && (
                <aside className={`cc-course-sidebar${rank === 5 ? " cc-course-sidebar--class-five" : ""}`} aria-label="Course lessons">
                  <div className="cc-course-sidebar__progress-panel">
                    <div className="cc-course-sidebar__metrics">
                      <span><b>{completedLessonCount}</b> completed</span>
                      <span><b>{Math.max(TOTAL_LESSONS - completedLessonCount, 0)}</b> remaining</span>
                    </div>
                    <div
                      className="cc-course-sidebar__progress"
                      role="progressbar"
                      aria-label="Course completion"
                      aria-valuemin="0"
                      aria-valuemax={TOTAL_LESSONS}
                      aria-valuenow={completedLessonCount}
                    >
                      <span style={{ width: `${(completedLessonCount / TOTAL_LESSONS) * 100}%` }} />
                    </div>
                  </div>

                  <div className="cc-course-sidebar__scroll" ref={sidebarScrollRef}>
                    <ul className="cc-course-lessons">
                      {courseInfo.contentData?.lessons?.map((lesson, index) => {
                        const isActive    = lesson.rank === rank;
                        const isCompleted = Boolean(courseInfo?.data?.[`aud${lesson.rank}`]);
                        return (
                          <React.Fragment key={index}>
                          <li
                            className={`cc-course-lesson${isActive ? " is-active" : ""}${isCompleted ? " is-complete" : ""}`}
                            aria-current={isActive ? "step" : undefined}
                            data-sidebar-active={isActive ? "true" : undefined}
                          >
                            {isCompleted && !isActive && (
                              <button
                                type="button"
                                className="cc-course-lesson__open"
                                aria-label={`Review Class ${lesson.rank}: ${lesson.title}`}
                                onClick={() => handleLessonSelect(lesson.rank)}
                                disabled={contentLoading || updateLoading || assessmentLoading || classFiveBudgetLoading || Boolean(budgetFormAction)}
                              />
                            )}
                            <span className="cc-course-lesson__icon" aria-hidden="true">
                              {isCompleted
                                ? <i className="fa-solid fa-check" />
                                : <i className="fa-solid fa-play" />}
                            </span>
                            <div className="cc-course-lesson__label">
                              <b>Class {lesson.rank}:</b>
                              <span>{lesson.title}</span>
                            </div>
                          </li>
                          {lesson.rank === 5 && rank === 5 && (
                            <ClassFiveSidebarItems
                              activeSubClass={subClass}
                              step={step}
                              progress={classFiveProgress}
                              onSelect={handleBudgetSelect}
                              videoCompleted={videoCompleted}
                              disabled={contentLoading || updateLoading || assessmentLoading || classFiveBudgetLoading || Boolean(budgetFormAction)}
                            />
                          )}
                          </React.Fragment>
                        );
                      })}
                    </ul>
                  </div>
                </aside>
              )}

              {/* ── Content area ── */}
              {(contentLoading || updateLoading) ? (
                <div
                  ref={courseLoadingRef}
                  tabIndex={-1}
                  role="status"
                  aria-label="Loading the next course section"
                  className="cc-course-content cc-course-content--loading"
                >
                  <ThreeDots height="40" width="80" radius="9" color="#4fa94d" ariaLabel="three-dots-loading"
                    wrapperStyle={{ display: "flex", justifyContent: "center" }} visible />
                </div>
              ) : (
                <section
                  ref={courseContentRef}
                  tabIndex={-1}
                  className={`cc-course-content${rank === 5 ? " cc-course-content--class-five" : ""}${rank === 5 && step === "class5form" ? " cc-course-content--class-five-form" : ""}${step === "class5report" ? " cc-course-content--class-five-report" : ""}${step === "quiz" ? " cc-course-content--quiz" : ""}${step === "lesson" && courseInfo?.contentData?.video ? " cc-course-content--video-lesson" : ""}`}
                >

                  {/* ══════════════════════════════════════════════════════════
                      STEP: QUIZ
                  ══════════════════════════════════════════════════════════ */}
                  {step === "quiz" && (
                    <QuizView
                      rank={rank}
                      quizKey={rank === 5 && formName === "debts" ? "5-debts" : rank}
                      lessonTitle={courseInfo.contentData.lessontitle}
                      totalLessons={TOTAL_LESSONS}
                      onSubmit={handleQuizComplete}
                      onBack={() => setStep(rank === 5 && formName === "debts" ? "class5form" : "lesson")}
                      onSaveLater={handleQuizSaveLater}
                    />
                  )}

                  {/* ══════════════════════════════════════════════════════════
                      STEP: AFTER-QUIZ FORM
                      class 12 → ComparisonOfOptionsForm
                  ══════════════════════════════════════════════════════════ */}
                  {step === "form" && rank === 12 && (
                    <ComparisonOfOptionsForm
                      onSubmit={handleComparisonSubmit}
                      onBack={() => setStep("lesson")}
                      onSaveLater={handleSaveLater}
                      loading={assessmentLoading || updateLoading}
                    />
                  )}

                  {/* ══════════════════════════════════════════════════════════
                      STEP: CLASS-6 STANDALONE FORM (no quiz)
                  ══════════════════════════════════════════════════════════ */}
                  {step === "class6form" && (
                    <HowGotHereFullForm
                      onSubmit={handleHowGotHereSubmit}
                      onBack={() => setStep("lesson")}
                      onSaveLater={handleSaveLater}
                      loading={assessmentLoading || updateLoading}
                      dataLoading={assessmentDataLoading}
                      initialData={assessmentInitialData}
                    />
                  )}

                  {/* Class 7 / Class 12 standalone Quick Knowledge Check */}
                  {step === "qkc" && (
                    <QuickKnowledgeCheckOverlay
                      question={rank === 12 ? CLASS12_QUESTION : CLASS7_QUESTION}
                      onCorrect={handleQKCCorrect}
                      onBack={() => setStep(rank === 12 ? "form" : "lesson")}
                      onSaveLater={handleQuizSaveLater}
                      onReplaySection={() => {
                        // This is a voluntary replay after the learner already watched the section.
                        // Keep Continue unlocked while the Class 7/Class 12 video starts again.
                        setVideoCompleted(true);
                        setStep("lesson");
                        window.requestAnimationFrame(() => {
                          window.scrollTo({ top: 0, behavior: "smooth" });
                        });
                      }}
                    />
                  )}

                  {/* ══════════════════════════════════════════════════════════
                      STEP: LESSON  (video + tabs + optional Formik form)
                  ══════════════════════════════════════════════════════════ */}
                  {step === "class5form" && formConfig && (
                    classFiveBudgetLoading ? (
                      <div className="cc-course-content--loading" role="status" aria-label="Loading saved budget information">
                        <ThreeDots height="40" width="80" radius="9" color="#4fa94d" ariaLabel="three-dots-loading" visible />
                      </div>
                    ) : (
                    <>
                      {isAssetsForm && (
                        <div className="cc-expense-stepper cc-assets-stepper" aria-label={`Assets step ${assetFormStep} of 5`}>
                          {[1, 2, 3, 4, 5].map((stepNumber) => (
                            <div className={`cc-expense-stepper__step${stepNumber < assetFormStep ? " is-complete" : ""}${stepNumber === assetFormStep ? " is-active" : ""}`} key={stepNumber}>
                              <span>{stepNumber}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <h1 className="cc-course-title">
                        {courseInfo.contentData.lessontitle}
                      </h1>
                      <p className="cc-course-meta">
                        Class {rank} of {TOTAL_LESSONS} - Pre-Filing Credit Counseling
                      </p>
                      <Formik
                        key={`class5-form-${formStorageKey}`}
                        initialValues={formInitialValues}
                        enableReinitialize
                        validationSchema={formSchema}
                        onSubmit={handleFormikSubmit}
                      >
                        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
                          <Form className="cc-class5-form-body" noValidate>
                            <formConfig.Component
                              values={values}
                              setFieldValue={setFieldValue}
                              errors={errors}
                              touched={touched}
                              activeStep={isAssetsForm ? assetFormStep : expenseFormStep}
                              reviewSummary={reviewSummary}
                            />
                            <BudgetValidationFeedback />
                            <div className={`footer-form-btn d-flex flex-wrap mt-4${isClassFiveMultiStepForm ? " cc-has-step-actions" : ""}`}>
                              <button
                                type="button"
                                className="btn_brnd cc-action-back mt-4 me-3"
                                onClick={() => {
                                  if (isMonthlyExpensesForm && expenseFormStep > 1) {
                                    setExpenseFormStep((current) => current - 1);
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                  } else if (isAssetsForm && assetFormStep > 1) {
                                    setAssetFormStep((current) => current - 1);
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                  } else if (isClassFiveVideoStep) {
                                    setStep("lesson");
                                    window.scrollTo({ top: 0, behavior: "smooth" });
                                  } else {
                                    handleBack();
                                  }
                                }}
                                disabled={updateLoading || classFiveBudgetLoading || isBudgetFormActionLoading || isSubmitting}
                              >
                                Go Back
                              </button>
                              <button
                                type="button"
                                className="btn_brnd cc-action-save-later mt-4"
                                onClick={() => handleSaveLater(values)}
                                disabled={updateLoading || classFiveBudgetLoading || isBudgetFormActionLoading || isSubmitting}
                              >
                                {budgetFormAction === "save-later" ? (
                                  <ThreeDots height="20" width="54" radius="9" color="#3f8a3d"
                                    ariaLabel="saving-budget" wrapperStyle={{ display: "block" }} visible />
                                ) : "Save and Return Later"}
                              </button>
                              <button
                                type={!isClassFiveMultiStepForm || isFinalMultiStepForm ? "submit" : "button"}
                                className="btn_brnd mt-4 me-3 btn-next cc-action-save-step"
                                onClick={isClassFiveMultiStepForm
                                  ? (isFinalMultiStepForm ? undefined : () => handleStepSaveAndContinue(values))
                                  : undefined}
                                disabled={updateLoading || classFiveBudgetLoading || isBudgetFormActionLoading || isSubmitting}
                              >
                                {budgetFormAction === "save-continue" || updateLoading || isSubmitting ? (
                                  <ThreeDots height="20" width="60" radius="9" color="#fff"
                                    ariaLabel="three-dots-loading" wrapperStyle={{ display: "block" }} visible />
                                ) : "Save and Continue"}
                              </button>
                            </div>
                          </Form>
                        )}
                      </Formik>
                    </>
                    )
                  )}

                  {step === "class5report" && isClassFiveReport && (
                    <ClassFiveBudgetReport
                      report={classFiveReport}
                      referral={reportReferral}
                      onReferralChange={(value) => {
                        setReportReferral(value);
                        setReportReferralAttempted(false);
                      }}
                      referralAttempted={reportReferralAttempted}
                      referralRef={reportReferralRef}
                      onContinue={handleClassFiveReportContinue}
                      onBack={() => {
                        setStep("lesson");
                        focusCourseTop();
                      }}
                      onSaveLater={() => handleSaveLater()}
                      loading={updateLoading || classFiveBudgetLoading}
                    />
                  )}

                  {step === "lesson" && rank === 2 && (
                    <RequiredDisclosures
                      onContinue={() => {
                        focusCourseTop("instant");
                        return advanceLesson();
                      }}
                      onBack={handleBack}
                      onSaveLater={() => handleSaveLater()}
                      loading={updateLoading || contentLoading}
                    />
                  )}

                  {step === "lesson" && rank !== 2 && (
                    <div className="cc-lesson-workspace">
                      <header className="cc-lesson-workspace__heading">
                        {courseInfo.contentData.lessontitle && (
                          <h1 className="cc-course-title">
                            {courseInfo.contentData.lessontitle}
                          </h1>
                        )}
                        {courseInfo.contentData.class && (
                          <p className="cc-course-meta">
                            Class {rank} of {TOTAL_LESSONS} - Pre-Filing Credit Counseling
                          </p>
                        )}

                      </header>
                      <div className="cc-lesson-workspace__media">
                        {/* Video */}
                        {courseInfo.contentData.video && (
                          <>
                          <video
                            ref={videoRef}
                            width="100%"
                            height="450"
                            src={courseInfo.contentData.video}
                            controlsList="nodownload noplaybackrate"
                            disablePictureInPicture
                            autoPlay
                            playsInline
                            preload="auto"
                            controls
                            onCanPlay={(event) => {
                              const playAttempt = event.currentTarget.play();
                              if (playAttempt?.catch) playAttempt.catch(() => {});
                            }}
                            onEnded={() => setVideoCompleted(true)}
                            onContextMenu={(e) => e.preventDefault()}
                            className="cc-course-video"
                          />
                            {rank === 1 && !videoCompleted && (
                              <p className="cc-course-video-note">
                                Please watch the full video to unlock {isClassFiveReport ? "View Report" : "Save and Continue"}.
                              </p>
                            )}
                            </>
                        )}

                        {rank === 4 && (
                          <div className="cc-checklist-resource">
                            <span className="cc-checklist-resource__icon" aria-hidden="true">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 3v12m-4-4 4 4 4-4M5 16v4h14v-4" />
                              </svg>
                            </span>
                            <div>
                              <a
                                className="cc-checklist-resource__link"
                                href={`${process.env.PUBLIC_URL || ""}/downloads/checklist.pdf`}
                                download="checklist.pdf"
                              >
                                Download the checklist
                              </a>
                              <p>Budget preparation worksheet <span aria-hidden="true">·</span> Fillable PDF</p>
                            </div>
                          </div>
                        )}

                        {rank === 5 && subClass === 1 && (
                          <div className="cc-checklist-resource">
                            <span className="cc-checklist-resource__icon" aria-hidden="true">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 3v12m-4-4 4 4 4-4M5 16v4h14v-4" />
                              </svg>
                            </span>
                            <div>
                              <a
                                className="cc-checklist-resource__link"
                                href={`${process.env.PUBLIC_URL || ""}/downloads/budget-worksheet.pdf?v=4`}
                                download="budget-worksheet.pdf"
                              >
                                Download the budget worksheet
                              </a>
                              <p>Optional worksheet <span aria-hidden="true">·</span> Printable PDF</p>
                            </div>
                          </div>
                        )}

                        {rank === 6 && (
                          <div className="cc-checklist-resource">
                            <span className="cc-checklist-resource__icon" aria-hidden="true">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 3v12m-4-4 4 4 4-4M5 16v4h14v-4" />
                              </svg>
                            </span>
                            <div>
                              <a
                                className="cc-checklist-resource__link"
                                href={`${process.env.PUBLIC_URL || ""}/documents/how-i-got-here-worksheet.pdf`}
                                download="how-i-got-here-worksheet.pdf"
                              >
                                Download the How I Got Here worksheet
                              </a>
                              <p>Print and fill out <span aria-hidden="true">·</span> PDF worksheet</p>
                            </div>
                          </div>
                        )}

                        {rank === 11 && (
                          <div className="cc-checklist-resource">
                            <span className="cc-checklist-resource__icon" aria-hidden="true">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M15 3h6v6m0-6L10 14M9 3H3v18h18v-6" />
                              </svg>
                            </span>
                            <div>
                              <a
                                className="cc-checklist-resource__link"
                                href="https://www.justice.gov/ust/bankruptcy-information-sheet-0"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Bankruptcy Information Sheet
                              </a>
                              <p>Opens in a new tab</p>
                            </div>
                          </div>
                        )}

                        {rank === 12 && (
                          <div className="cc-checklist-resource">
                            <span className="cc-checklist-resource__icon" aria-hidden="true">
                              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M12 3v12m-4-4 4 4 4-4M5 16v4h14v-4" />
                              </svg>
                            </span>
                            <div>
                              <a
                                className="cc-checklist-resource__link"
                                href={`${process.env.PUBLIC_URL || ""}/downloads/comparison-of-options.pdf`}
                                download="comparison-of-options.pdf"
                              >
                                Download Comparison Worksheet (PDF)
                              </a>
                              <p>Print and fill out <span aria-hidden="true">·</span> PDF worksheet</p>
                            </div>
                          </div>
                        )}

                        {isClassFiveVideoStep && !courseInfo.contentData.video && (
                          <div className="alert alert-warning" role="alert">
                            This video is not available right now. Please contact support before continuing.
                          </div>
                        )}

                      </div>
                      {(frontendLesson?.transcript?.length > 0 || (!frontendLesson && (courseInfo.contentData.text || courseInfo.contentData.readMore))) && (
                      <aside className="cc-lesson-workspace__transcript" aria-label="Lesson transcript and reading" tabIndex={0} key={`${rank}-${subClass}`}>
                        {/* Frontend-owned document content for every non-budget lesson */}
                        {frontendLesson?.transcript?.length > 0 && (
                          <LessonDocumentContent lesson={frontendLesson} />
                        )}

                        {/* Existing backend content remains available only for the skipped class-5 budget branch */}
                        {!frontendLesson && (courseInfo.contentData.text || courseInfo.contentData.readMore) && (
                          <div className="cc-course-resource">
                            <ul className="nav nav-tabs cc-course-tabs" role="tablist">
                              <li className="nav-item" role="presentation">
                                <button
                                  className={`nav-link ${activeTab === "transcript" ? "active" : ""}`}
                                  type="button"
                                  onClick={() => setActiveTab("transcript")}
                                >
                                  Transcript
                                </button>
                              </li>
                              {courseInfo.contentData.readMore && (
                                <li className="nav-item" role="presentation">
                                  <button
                                    className={`nav-link ${activeTab === "reading" ? "active" : ""}`}
                                    type="button"
                                    onClick={() => setActiveTab("reading")}
                                  >
                                    Reading
                                  </button>
                                </li>
                              )}
                            </ul>
                            {activeTab === "reading" && (
                              <div className="tab-pane active cc-course-copy">
                                {courseInfo.contentData.text && (
                                  <div
                                    dangerouslySetInnerHTML={{ __html: courseInfo.contentData.text }} />
                                )}
                                {courseInfo.contentData.readMore && (
                                  <div className="mt-3"
                                    dangerouslySetInnerHTML={{ __html: courseInfo.contentData.readMore }} />
                                )}
                              </div>
                            )}
                            {activeTab === "transcript" && courseInfo.contentData.text && (
                              <div className="tab-pane active cc-course-copy">
                                <div
                                  dangerouslySetInnerHTML={{ __html: courseInfo.contentData.text }} />
                              </div>
                            )}
                          </div>
                        )}

                      </aside>
                      )}

                      <div className="footer-form-btn d-flex flex-wrap mt-4 cc-lesson-workspace__actions">
                        {rank > 1 && (
                          <button className="btn_brnd cc-action-back mt-4 me-3" onClick={handleBack} disabled={updateLoading}>
                            Go Back
                          </button>
                        )}
                        <button className="btn_brnd cc-action-save-later mt-4 me-3" onClick={() => handleSaveLater()} disabled={updateLoading}>
                          Save and Return Later
                        </button>
                        <span
                          className="cc-video-continue"
                          tabIndex={isVideoContinueLocked ? 0 : undefined}
                          aria-describedby={isVideoContinueLocked ? "cc-video-continue-tooltip" : undefined}
                        >
                        {isVideoContinueLocked && (
                          <span id="cc-video-continue-tooltip" role="tooltip" className="cc-video-continue__tooltip">
                            This button will become active once the video is complete.
                          </span>
                        )}
                        <button
                          className="btn_brnd mt-4 btn-next"
                          onClick={handleVideoEnd}
                          disabled={updateLoading || isVideoContinueLocked}
                          aria-describedby={isVideoContinueLocked ? "cc-video-continue-tooltip" : undefined}
                        >
                          {isClassFiveReport
                            ? "View Report"
                            : rank === 5 && formConfig
                            ? "Save and continue"
                            : frontendLesson?.continueLabel || (
                                rank === TOTAL_LESSONS ? "Complete Course" : "Save and Continue"
                              )}
                        </button>
                        </span>
                      </div>
                    </div>
                  )}

                </section>
              )}
            </div>
          </div>
        </main>
      ) : (
        <main className="theme_container cc-course-complete">
          <div className="cc-course-complete__card" role="alert">
            <h1>Course Unavailable</h1>
            <p>We could not load this course right now. Please try again.</p>
            <button type="button" className="btn_brnd" onClick={fetchCourseInfo}>
              Try Again
            </button>
          </div>
        </main>
      )}
      {!thirdParty && <Footer />}
    </div>
  );
};

export default CCLatestCourse;
